import React, { useState, useEffect, useCallback, memo, useRef, useMemo } from 'react';
import ShippingPreparationModal from '../components/ShippingPreparationModal';
import { VariableSizeList } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import productLinksService from '../services/ProductLinksService'
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TextField,
  Pagination,
  Stack,
  Chip,
  IconButton,
  Grid,
  Alert,
  Tooltip,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  LinearProgress
} from '@mui/material';
import _ from 'lodash';

import {
  Refresh as RefreshIcon,
  LocalShipping as ShippingIcon,
  Print as PrintIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  SortByAlpha as SortIcon,
  LocalShipping as LocalShippingIcon,
  ContentCopy as ContentCopyIcon,
  Close as CloseIcon,
  LocalShipping,
  Search as SearchIcon,
  ViewList as ViewListIcon,
  Category as CategoryIcon,
  ShoppingCart as ShoppingCartIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import moment from 'moment';
import HomeDeliveryModal from '../components/HomeDeliveryModal';

import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import ExcelLikeField from '../components/ExcelLikeField';
import ProductImageGallery from '../components/ProductImageGallery';
import { Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon } from '@mui/icons-material';
import { io, Socket } from 'socket.io-client';

// Performance tracking
let lastRenderTime = Date.now();

declare global {
  interface Window {
    __trackedItems?: Set<string>;
  }
}

// Interfaces
interface OrderItem {
  id: string;
  orderId: string;
  productName: string;
  itemSpec: string;
  quantity: number;
  imageUrl: string;
  sku: string;
  productKey?: string;
  received: boolean;
  missingCount: number;
  referenceImage?: string;
  tag?: string;
  note: string;
  original: any;
  orderLink?: string;
  receivedBy?: string;
  receivedAt?: string;
  trackingNumber?: string;
  paymentNo?: string;
  cvsValidationNo?: string;
  ecpayLogisticsId?: string;
  product_images?: ProductImage[];
  lineItemId?: string;
}

interface ProductInfo {
  link: string;
  allLinks?: string[];
}

interface TrackingChanges {
  received?: boolean;
  missingCount?: number;
  note?: string;
  tag?: string;
  referenceImage?: string;
  receivedBy?: string;
  receivedAt?: string;
}

interface ProductMetadata {
  product_key: string;
  tag: string;
  reference_image?: string;
  referenceImage?: string;
  createdAt: string;
  lastUpdated: string;
}

interface ExcelProductData {
  id: string;
  link: string;
  allLinks?: string[];
  [key: string]: any;
}

interface HoverZoomImageProps {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  borderColor?: string;
}

interface SimpleHoverZoomProps {
  src: string;
  alt: string;
  width?: number;
  height?: number;
  zoomSize?: number;
  borderColor?: string;
}

interface HsinchuLabelData {
  trackingNumber?: string;
  recipientName?: string;
  recipientAddress?: string;
  weight?: number;
  [key: string]: any; // Allow additional properties
}

interface ProductImage {
  id: number;
  src: string;
  alt?: string;
  position: number;
}

interface ShopifyOrdersPageProps {
  socket: Socket | null;
  productMetadata: { [key: string]: ProductMetadata };
  setProductMetadata: React.Dispatch<React.SetStateAction<{ [key: string]: ProductMetadata }>>;
  onOrdersUpdate?: (orders: any[]) => void;
}

// ── Design System (warm neutral palette, matching OrdersPage) ──
const S = {
  // Page container
  page: {
    width: '100%',
    minHeight: '100vh',
    bgcolor: '#ffffff',
    fontFamily: '"DM Sans", "Noto Sans TC", sans-serif',
    pb: 4,
  },

  // Header
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    mb: 2.5,
    px: { xs: 2, md: 3 },
    pt: { xs: 2, md: 2.5 },
    pb: 2,
    bgcolor: '#ffffff',
    borderBottom: '1px solid #e8e5e1',
    boxShadow: '0 1px 0 rgba(0,0,0,0.03)',
    borderRadius: { xs: 0, md: '0 0 16px 16px' },
    flexWrap: 'wrap' as const,
    gap: 1.5,
  },
  headerTitle: {
    fontWeight: 800,
    fontSize: { xs: '1.35rem', md: '1.6rem' },
    color: '#1a1a1a',
    letterSpacing: '-0.03em',
    lineHeight: 1.2,
    fontFamily: '"DM Sans", "Noto Sans TC", sans-serif',
  },

  // Pill tab toggle
  tabContainer: {
    bgcolor: '#f0ede9',
    p: '3px',
    borderRadius: '12px',
    display: 'inline-flex',
    gap: '3px',
  },
  tabButton: (active: boolean) => ({
    borderRadius: '10px',
    boxShadow: active ? '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)' : 'none',
    bgcolor: active ? '#ffffff' : 'transparent',
    color: active ? '#1a1a1a' : '#8c8580',
    px: { xs: 2, md: 2.5 },
    py: 0.75,
    fontWeight: active ? 700 : 500,
    textTransform: 'none' as const,
    minWidth: 72,
    fontSize: '0.875rem',
    transition: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      bgcolor: active ? '#ffffff' : 'rgba(0,0,0,0.04)',
      color: active ? '#1a1a1a' : '#5c5854',
    },
  }),

  // Action button (header)
  headerBtn: {
    borderRadius: '10px',
    borderColor: '#e8e5e1',
    color: '#5c5854',
    fontWeight: 600,
    textTransform: 'none' as const,
    fontSize: '0.85rem',
    transition: 'all 0.15s ease',
    '&:hover': { borderColor: '#d1cdc8', bgcolor: 'rgba(0,0,0,0.02)' },
  },
  headerBtnPrimary: {
    borderRadius: '10px',
    fontWeight: 600,
    textTransform: 'none' as const,
    fontSize: '0.85rem',
    bgcolor: '#1a1a1a',
    transition: 'all 0.15s ease',
    '&:hover': { bgcolor: '#333' },
  },

  // Filter panel
  filterPanel: {
    p: { xs: 2, md: 2.5 },
    mx: { xs: 2, md: 3 },
    mb: 2,
    border: '1px solid #e8e5e1',
    borderRadius: '14px',
    bgcolor: '#fafaf8',
    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.02)',
  },
  searchField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      bgcolor: '#ffffff',
      fontSize: '1.05rem',
      transition: 'none',
      fontFamily: '"DM Sans", "Noto Sans TC", sans-serif',
      '& fieldset': { borderColor: '#e8e5e1' },
      '&:hover fieldset': { borderColor: '#d1cdc8' },
      '&.Mui-focused fieldset': { borderColor: '#8c8580', borderWidth: '1.5px' },
    },
  },
  filterSelect: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '10px',
      bgcolor: '#ffffff',
      '& fieldset': { borderColor: '#e8e5e1' },
      '&:hover fieldset': { borderColor: '#d1cdc8' },
      '&.Mui-focused fieldset': { borderColor: '#8c8580' },
    },
  },
  filterChip: {
    borderRadius: '8px',
    fontWeight: 600,
    fontSize: '0.8rem',
    height: 28,
    '& .MuiChip-deleteIcon': {
      color: 'inherit',
      opacity: 0.6,
      fontSize: 16,
      '&:hover': { opacity: 1 },
    },
  },

  // Batch toolbar
  batchToolbar: {
    mb: 2,
    mx: { xs: 2, md: 3 },
    p: 2,
    bgcolor: '#ffffff',
    border: '1px solid #e8e5e1',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  },

  // Order card
  orderCard: (status: 'ready' | 'partial' | 'pending' | 'shipped') => ({
    borderRadius: '16px',
    border: '1px solid #e8e5e1',
    bgcolor: '#ffffff',
    boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 1px 2px rgba(0,0,0,0.02)',
    overflow: 'visible',
    display: 'flex',
    flexDirection: 'column' as const,
    minHeight: 'fit-content',
    transition: 'box-shadow 0.2s ease, transform 0.2s ease',
    borderLeft: status === 'ready' ? '4px solid #22c55e'
      : status === 'partial' ? '4px solid #f59e0b'
      : status === 'shipped' ? '4px solid #3b82f6'
      : '4px solid transparent',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(0,0,0,0.06), 0 1px 3px rgba(0,0,0,0.04)',
      transform: 'translateY(-1px)',
    },
    '@media (max-width: 600px)': { minHeight: 'auto' },
  }),

  // Status chip
  statusChip: {
    fontWeight: 700,
    fontSize: '0.7rem',
    height: 26,
    borderRadius: '8px',
    letterSpacing: '0.02em',
  },

  // Table
  tableHeader: {
    bgcolor: '#fafaf8',
    '& .MuiTableCell-head': {
      fontWeight: 700,
      fontSize: '0.75rem',
      textTransform: 'uppercase' as const,
      letterSpacing: '0.06em',
      color: '#8c8580',
      borderBottom: '1px solid #e8e5e1',
      py: 1.5,
      px: 2,
      fontFamily: '"DM Sans", "Noto Sans TC", sans-serif',
    },
  },
  tableRow: (received: boolean) => ({
    bgcolor: received ? '#f0fdf4' : 'transparent',
    transition: 'background-color 0.15s ease',
    '&:hover': {
      bgcolor: received ? '#dcfce7' : '#fafaf8',
    },
    '& .MuiTableCell-root': {
      borderBottom: '1px solid #f3f1ee',
      py: 2,
      px: 2,
      fontSize: '0.9rem',
    },
  }),

  // Monospace
  mono: {
    fontFamily: '"DM Mono", "SF Mono", monospace',
  },
};

const ShopifyOrdersPage: React.FC<ShopifyOrdersPageProps> = ({
  socket,
  productMetadata,
  setProductMetadata,
  onOrdersUpdate
}) => {



  const { shopifyAuthenticated, currentUser } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  // Core state
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState<any[]>([]);
  const [parsedOrders, setParsedOrders] = useState<any[]>([]);
  const [allParsedOrders, setAllParsedOrders] = useState<any[]>([]);

  // UI state
  const [statusFilter, setStatusFilter] = useState('All');
  const [sortOrder, setSortOrder] = useState('oldest');
  const [searchFilter, setSearchFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [ordersPerPage] = useState(30);
  const [colorFilter, setColorFilter] = useState('All');
  const [sizeFilter, setSizeFilter] = useState('All');
  const [tagFilter, setTagFilter] = useState('All');

  // Modal and dialog state
  const [shippingDialogData, setShippingDialogData] = useState<any>(null);

  // Batch printing state
  const [selectedOrders, setSelectedOrders] = useState<string[]>([]);
  const [batchPrintDialogOpen, setBatchPrintDialogOpen] = useState(false);
  const [batchPrintLoading, setBatchPrintLoading] = useState(false);

  // Performance optimization state
  const [isEditing, setIsEditing] = useState(false);
  const fetchingRef = useRef(false);

  // Refs
  const scrollPositionRef = useRef(0);
  const listRef = useRef<any>(null);
  const measuredHeightsRef = useRef<{ [key: number]: number }>({});
  const [searchInput, setSearchInput] = useState('');
  const [isSearchPending, setIsSearchPending] = useState(false);
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set());
  const [homeDeliveryDialogData, setHomeDeliveryDialogData] = useState<any>(null);
  const [receivedStatusFilter, setReceivedStatusFilter] = useState('All');
  const [justCompletedOrders, setJustCompletedOrders] = useState<Record<string, boolean>>({});
  const [fulfillmentFilter, setFulfillmentFilter] = useState('unfulfilled');
  const [showHiddenOrders, setShowHiddenOrders] = useState(false);
  const [isLoadingFromCache, setIsLoadingFromCache] = useState(false);
  const [backgroundRefreshing, setBackgroundRefreshing] = useState(false);

  // View mode state for product grouping
  const [viewMode, setViewMode] = useState<'orders' | 'products'>('orders');
  // Auto-compute last ordering cutoff: Wed 20:00 or Sun 20:00 (whichever is most recent)
  const getLastOrderingCutoff = useCallback(() => {
    const now = moment();
    const day = now.day(); // 0=Sun, 3=Wed
    const lastWed = moment().day(day >= 3 ? 3 : 3 - 7).hour(20).minute(0).second(0);
    if (lastWed.isAfter(now)) lastWed.subtract(7, 'days');
    const lastSun = moment().day(day >= 0 ? 0 : 0 - 7).hour(20).minute(0).second(0);
    if (lastSun.isAfter(now)) lastSun.subtract(7, 'days');
    return lastWed.isAfter(lastSun) ? lastWed : lastSun;
  }, []);
  const [groupingAfterDate, setGroupingAfterDate] = useState(() => getLastOrderingCutoff().format('YYYY-MM-DDTHH:mm'));
  const [expandedGroupOrderIds, setExpandedGroupOrderIds] = useState<Set<string>>(new Set());
  const [linkBatchIndex, setLinkBatchIndex] = useState(0);

  const debouncedSearch = useCallback(
    _.debounce((searchTerm: string) => {
      setSearchFilter(searchTerm);
      setIsSearchPending(false);
    }, 500),
    []
  );

  // Batch printing functions
  const toggleOrderSelection = useCallback((orderId: string) => {
    setSelectedOrders(prev => {
      if (prev.includes(orderId)) {
        return prev.filter(id => id !== orderId);
      } else {
        return [...prev, orderId];
      }
    });
  }, []);



  const hasNumbersOnly = useCallback((tag: string) => {
    if (!tag || tag.trim() === '') return false;
    const hasDigit = /\d/.test(tag);
    const onlyNumbersAndSeparators = /^[\d\s\-_.,]*$/.test(tag);
    return hasDigit && onlyNumbersAndSeparators;
  }, []);

  const selectAllEligibleOrders = useCallback(() => {
    const eligibleOrders = parsedOrders
      .filter(order => order.tracking_number || order.payment_no)
      .map(order => order.orderId);
    setSelectedOrders(eligibleOrders);
  }, [parsedOrders]);

  const clearAllSelections = useCallback(() => {
    setSelectedOrders([]);
  }, []);

  const fixChineseNameOrder = useCallback((firstName: string, lastName: string) => {
    if (!firstName && !lastName) return '客戶';

    // If both names exist and they appear to be Chinese (single characters)
    if (firstName && lastName) {
      // Check if this looks like reversed Chinese names
      if (firstName.length === 1 && lastName.length === 1) {
        // Shopify often stores Chinese names as: first_name="名承" last_name="范"
        // But correct order should be: "范名承" (family name first)
        return `${lastName}${firstName}`;
      }

      // For Western names or longer names, keep original order
      if (firstName.length > 1 || lastName.length > 1) {
        return `${firstName} ${lastName}`.trim();
      }
    }

    // If only one name is provided
    return (firstName || lastName || '客戶').trim();
  }, []);

  // Helper function to extract phone number from Shopify order
  const getCustomerPhone = useCallback((order: any) => {
    // Priority order for getting phone number:

    // 1. Shipping address phone (most reliable for delivery)
    if (order.shipping_address?.phone) {
      return order.shipping_address.phone;
    }

    // 2. Billing address phone
    if (order.billing_address?.phone) {
      return order.billing_address.phone;
    }

    // 3. Customer phone
    if (order.customer?.phone) {
      return order.customer.phone;
    }

    // 4. Check if phone is in customer default address
    if (order.customer?.default_address?.phone) {
      return order.customer.default_address.phone;
    }

    // 5. Default fallback (you may want to collect this manually)
    return '0912345678'; // Default phone number - consider making this configurable
  }, []);

  const isExchangeOrder = useCallback((orderNumber: string | number): boolean => {
    const orderStr = String(orderNumber);
    // Exchange orders: start with 9 and have exactly 8 digits
    return /^9\d{7}$/.test(orderStr);
  }, []);

  const handleBatchPrint = useCallback(() => {
    if (selectedOrders.length === 0) {
      enqueueSnackbar('請選擇要列印的訂單', { variant: 'warning' });
      return;
    }
    setBatchPrintDialogOpen(true);
  }, [selectedOrders.length, enqueueSnackbar]);

  const confirmBatchPrint = useCallback(async () => {
    try {
      setBatchPrintLoading(true);

      // Group selected orders by logistics sub type
      const ordersByType: Record<string, any[]> = {};

      selectedOrders.forEach(orderId => {
        const order = parsedOrders.find(o => o.orderId === orderId);
        if (order && (order.tracking_number || order.payment_no)) {
          const logisticsType = order.logistics_sub_type || 'UNIMARTC2C';
          if (!ordersByType[logisticsType]) {
            ordersByType[logisticsType] = [];
          }
          ordersByType[logisticsType].push(order);
        }
      });

      // Print each group separately
      for (const [logisticsType, orders] of Object.entries(ordersByType)) {
        try {
          console.log(`Batch printing ${orders.length} orders with logistics type: ${logisticsType}`);

          const logisticsIds: string[] = [];
          const paymentNos: string[] = [];
          const validationNos: string[] = [];

          orders.forEach((order: any) => {
            if (order.ecpay_logistics_id && order.payment_no) {
              logisticsIds.push(order.ecpay_logistics_id);
              paymentNos.push(order.payment_no);

              // Handle validation numbers based on logistics type
              if (logisticsType === 'FAMIC2C') {
                validationNos.push('');
              } else {
                if (order.cvs_validation_no) {
                  validationNos.push(order.cvs_validation_no);
                } else if (order.payment_no && order.payment_no.length >= 4) {
                  validationNos.push(order.payment_no.slice(-4));
                } else {
                  validationNos.push('');
                }
              }
            }
          });

          // Call batch print API
          const response = await api.post('/api/ecpay/logistics/batch-print', {
            logisticsIds: logisticsIds.join(','),
            paymentNos: paymentNos.join(','),
            validationNos: validationNos.join(','),
            documentType: logisticsType,
            orderIds: orders.map(o => o.orderId)
          });

          if (response.data) {
            const blob = new Blob([response.data], { type: 'text/html' });
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank');
            setTimeout(() => URL.revokeObjectURL(url), 100);
          }

          enqueueSnackbar(
            `${orders.length} 張 ${logisticsType === 'FAMIC2C' ? '全家' : '7-ELEVEN'} 出貨單已開啟`,
            { variant: 'success' }
          );

        } catch (error) {
          console.error(`Error batch printing ${logisticsType}:`, error);
          enqueueSnackbar(`批次列印 ${logisticsType} 失敗`, { variant: 'error' });
        }
      }

      // Clear selections after successful print
      setSelectedOrders([]);

    } catch (error) {
      console.error('Batch print error:', error);
      enqueueSnackbar('批次列印失敗', { variant: 'error' });
    } finally {
      setBatchPrintLoading(false);
      setBatchPrintDialogOpen(false);
    }
  }, [selectedOrders, parsedOrders, enqueueSnackbar]);

  // Batch Actions Toolbar Component
  const BatchActionsToolbar = memo(() => {
    const eligibleOrdersCount = parsedOrders.filter(order =>
      order.tracking_number || order.payment_no
    ).length;

    if (eligibleOrdersCount === 0) return null;

    return (
      <Box sx={S.batchToolbar}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2">
            批次操作: 已選擇 {selectedOrders.length} / {eligibleOrdersCount} 個可列印訂單
          </Typography>
          <Button
            size="small"
            onClick={selectAllEligibleOrders}
            disabled={selectedOrders.length === eligibleOrdersCount}
          >
            全選
          </Button>
          <Button
            size="small"
            onClick={clearAllSelections}
            disabled={selectedOrders.length === 0}
          >
            清除選擇
          </Button>
        </Box>

        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={handleBatchPrint}
          disabled={selectedOrders.length === 0}
          color="primary"
        >
          批次列印 ({selectedOrders.length})
        </Button>
      </Box>
    );
  });



  // Batch Print Dialog Component
  const BatchPrintDialog = memo(() => {
    const selectedOrderDetails = selectedOrders.map(orderId =>
      parsedOrders.find(o => o.orderId === orderId)
    ).filter(Boolean);

    // Group by logistics type for display
    const ordersByType: Record<string, any[]> = selectedOrderDetails.reduce((acc, order) => {
      const logisticsType = order.logistics_sub_type || 'UNIMARTC2C';
      if (!acc[logisticsType]) {
        acc[logisticsType] = [];
      }
      acc[logisticsType].push(order);
      return acc;
    }, {} as Record<string, any[]>);

    return (
      <Dialog
        open={batchPrintDialogOpen}
        onClose={() => setBatchPrintDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          批次列印確認
          <IconButton
            onClick={() => setBatchPrintDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            您即將批次列印 {selectedOrders.length} 張出貨單。不同超商類型將分別開啟新視窗。
          </Alert>

          {Object.entries(ordersByType).map(([logisticsType, orders]) => (
            <Box key={logisticsType} sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                <PrintIcon sx={{ mr: 1 }} />
                {logisticsType === 'FAMIC2C' ? '全家超商' : '7-ELEVEN'}
                <Chip label={`${orders.length} 張`} size="small" sx={{ ml: 1 }} />
              </Typography>

              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2, borderRadius: '12px', border: '1px solid #e8e5e1' }}>
                <Table size="small">
                  <TableHead sx={S.tableHeader}>
                    <TableRow>
                      <TableCell>訂單編號</TableCell>
                      <TableCell>客戶</TableCell>
                      <TableCell>門市</TableCell>
                      <TableCell>物流編號</TableCell>
                      <TableCell>寄貨編號</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {orders.map((order: any) => (
                      <TableRow key={order.orderId}>
                        <TableCell>#{order.orderId}</TableCell>
                        <TableCell>{order.customer || 'N/A'}</TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {order.store_name || 'N/A'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {order.store_id || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: '"DM Mono", "SF Mono", monospace' }}>
                            {order.ecpay_logistics_id || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: '"DM Mono", "SF Mono", monospace' }}>
                            {order.payment_no || 'N/A'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          ))}

          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>注意事項：</strong>
              <br />
              • 不同超商類型將分別開啟新的列印視窗
              <br />
              • 請確保您的瀏覽器允許彈出視窗
              <br />
              • 列印完成後，此對話框將自動關閉並清除選擇
            </Typography>
          </Alert>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setBatchPrintDialogOpen(false)}
            disabled={batchPrintLoading}
          >
            取消
          </Button>
          <Button
            onClick={confirmBatchPrint}
            variant="contained"
            disabled={batchPrintLoading}
            startIcon={batchPrintLoading ? <CircularProgress size={20} /> : <PrintIcon />}
          >
            {batchPrintLoading ? '處理中...' : `確認列印 ${selectedOrders.length} 張`}
          </Button>
        </DialogActions>
      </Dialog>
    );
  });

  // Calculate pagination values
  const totalPages = Math.ceil(parsedOrders.length / ordersPerPage);

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);

    // Clear height cache and reset virtual list
    measuredHeightsRef.current = {};
    if (listRef.current) {
      listRef.current.resetAfterIndex(0, true);
      listRef.current.scrollTo(0);
    }
  };

  const calculateOrderTotal = useCallback((order: any): number => {
    let totalAmount = 0;

    if (order?.original?.current_total_price) {
      totalAmount = Math.floor(parseFloat(order.original.current_total_price) || 0);
      return totalAmount;
    }

    if (order?.original?.total_price_set?.shop_money?.amount) {
      totalAmount = Math.floor(parseFloat(order.original.total_price_set.shop_money.amount) || 0);
      return totalAmount;
    }

    if (order?.original?.subtotal_price && order?.original?.total_discounts) {
      const subtotal = parseFloat(order.original.subtotal_price) || 0;
      const discounts = parseFloat(order.original.total_discounts) || 0;
      totalAmount = Math.floor(subtotal - discounts);
      return totalAmount;
    }

    if (order?.original?.total_price) {
      totalAmount = Math.floor(parseFloat(order.original.total_price) || 0);
      return totalAmount;
    }

    return 0;
  }, []);

  const handleQuickShip = useCallback(async (order: any) => {
    try {
      enqueueSnackbar('正在快速建立物流單...', { variant: 'info' });

      // Validate that order has necessary information
      if (!order.store_id) {
        enqueueSnackbar('訂單缺少超商門市資訊', { variant: 'error' });
        return;
      }

      // Determine logistics type
      const logisticsType = order.logistics_sub_type ||
        (order.store_id && (order.store_id.startsWith('F') || /^[A-Z]\d{5}$/.test(order.store_id))
          ? 'FAMIC2C' : 'UNIMARTC2C');

      // Calculate order total
      let totalAmount = calculateOrderTotal(order);

      if ((!totalAmount || totalAmount <= 0) && ['paid', 'partially_refunded', 'refunded'].includes(order.status)) {
        totalAmount = 100; // 設定 1000 元作為物流申報價值
      }

      if (!totalAmount || totalAmount <= 0) {
        enqueueSnackbar('無法計算訂單金額', { variant: 'error' });
        return;
      }



      // Extract and format customer information
      const shippingAddress = order?.original?.shipping_address || {};
      const customer = order?.original?.customer || {};

      // Format customer name using the same logic as modal
      const formatChineseName = (firstName: string, lastName: string): string => {
        const chinesePattern = /[\u4e00-\u9fff]/;

        if (chinesePattern.test(firstName) || chinesePattern.test(lastName)) {
          return `${lastName || ''}${firstName || ''}`.trim();
        } else {
          return `${firstName || ''} ${lastName || ''}`.trim();
        }
      };

      // Format phone number using the same logic as modal
      const formatTaiwanPhone = (phone: string): string => {
        if (!phone) return '';

        let cleanPhone = phone.replace(/\D/g, '');

        if (cleanPhone.startsWith('886')) {
          cleanPhone = '0' + cleanPhone.substring(3);
        }

        if (!cleanPhone.startsWith('0') && cleanPhone.length === 9) {
          cleanPhone = '0' + cleanPhone;
        }

        return cleanPhone;
      };



      // Sanitize goods name
      const sanitizeGoodsName = (goodsName: string): string => {
        if (!goodsName) return 'Goods';
        const cleaned = goodsName.replace(/[^\w\s\u4e00-\u9fff,.]/g, '');
        return cleaned.length > 25 ? `${cleaned.substring(0, 22)}...` : cleaned;
      };

      const customerName = formatChineseName(
        shippingAddress.first_name || '',
        shippingAddress.last_name || ''
      );

      const customerPhone = formatTaiwanPhone(
        shippingAddress.phone || customer.phone || ''
      );

      // Validate formatted data
      if (!customerName || customerName.length > 10) {
        enqueueSnackbar('客戶姓名無效或超過10個字元', { variant: 'error' });
        return;
      }

      if (!customerPhone || customerPhone.length !== 10) {
        enqueueSnackbar('客戶電話號碼無效（需要10位數字）', { variant: 'error' });
        return;
      }

      // Generate items description
      const itemsDescription = sanitizeGoodsName(
        order.items
          .slice(0, 3)
          .map((item: OrderItem) => `${item.productName.split(' ')[0]} x ${item.quantity}`)
          .join(', ') + (order.items.length > 3 ? ` + ${order.items.length - 3} more` : '')
      );

      console.log('Quick ship data:', {
        customerName,
        customerPhone,
        itemsDescription,
        totalAmount,
        logisticsType
      });

      // Create logistics order with formatted data
      const response = await api.post(`/api/ecpay/logistics/create-for-shopify`, {
        orderId: String(order.orderNumber),
        storeId: order.store_id,
        customerName: customerName,
        customerPhone: customerPhone,
        customerEmail: customer.email || '',
        logisticsSubType: logisticsType,
        goodsAmount: totalAmount,
        storeName: order.store_name || '',
        isCollection: ['paid', 'partially_refunded', 'refunded'].includes(order.status) ? 'N' : 'Y',
        goodsName: itemsDescription,
        source: 'shopify_quick'
      });

      if (response.data?.success) {
        // Calculate display tracking number
        let displayTrackingNumber = '';
        if (response.data.trackingNumber) {
          displayTrackingNumber = response.data.trackingNumber;
        } else if (response.data.paymentNo) {
          if (logisticsType === 'FAMIC2C') {
            displayTrackingNumber = response.data.paymentNo;
          } else {
            displayTrackingNumber = response.data.validationNo
              ? `${response.data.paymentNo}${response.data.validationNo}`
              : response.data.paymentNo;
          }
        }

        // Update orders with tracking info
        const updateOrdersWithTracking = (prevOrders: any[]) => {
          return prevOrders.map(o => {
            if (o.orderId === order.orderId) {
              const updatedItems = o.items.map((item: OrderItem) => ({
                ...item,
                trackingNumber: response.data.trackingNumber || response.data.logisticsId,
                paymentNo: response.data.paymentNo,
                cvsValidationNo: response.data.validationNo,
                ecpayLogisticsId: response.data.logisticsId,
                logistics_sub_type: logisticsType,
                store_id: order.store_id,
                store_name: order.store_name
              }));

              return {
                ...o,
                items: updatedItems,
                hasTrackingInfo: true,
                tracking_number: displayTrackingNumber,
                payment_no: response.data.paymentNo,
                cvs_validation_no: response.data.validationNo,
                ecpay_logistics_id: response.data.logisticsId,
                logistics_sub_type: logisticsType
              };
            }
            return o;
          });
        };

        setParsedOrders(updateOrdersWithTracking);
        setAllParsedOrders(updateOrdersWithTracking);

        enqueueSnackbar(`快速出貨成功！追蹤號碼: ${displayTrackingNumber}`, {
          variant: 'success',
          autoHideDuration: 6000
        });

        // Emit socket event for real-time updates
        if (socket) {
          socket.emit('shipping-label-created', {
            shop: 'shopify',
            orderId: order.orderId,
            trackingNumber: displayTrackingNumber,
            logisticsId: response.data.logisticsId,
            paymentNo: response.data.paymentNo,
            validationNo: response.data.validationNo,
            logisticsSubType: logisticsType,
            storeId: order.store_id,
            storeName: order.store_name,
            updatedBy: currentUser?.name || localStorage.getItem('userName') || 'Unknown user',
            timestamp: new Date().toISOString()
          });
        }
        try {
          const userId = currentUser?.id?.toString() || localStorage.getItem('userId');
          const userName = currentUser?.name || localStorage.getItem('userName') || 'Unknown user';

          import('../services/employeeTrackingService').then(module => {
            const trackingService = module.default;
            trackingService.trackOrderShipped(
              order.orderId,
              order.items?.length || 0,
              {
                platform: 'shopify',
                method: 'quick_ship',
                trackingNumber: displayTrackingNumber,
                logisticsType: logisticsType,
                storeId: order.store_id,
                storeName: order.store_name,
                totalItems: order.items?.length || 0,
                user_id: userId,
                user_name: userName
              }
            );
          }).catch(err => {
            console.error('Error loading employee tracking service:', err);
          });
        } catch (trackingError) {
          console.error('Error tracking quick ship action:', trackingError);
        }

        try {
          enqueueSnackbar('正在標記訂單為已出貨...', { variant: 'info' });

          const fulfillResponse = await api.post(`/api/shopify/orders/${order.orderNumber}/fulfill`, {
            trackingNumber: displayTrackingNumber,
            trackingCompany: logisticsType === 'FAMIC2C' ? 'Family Mart' : '7-ELEVEN'
          });

          if (fulfillResponse.data?.success) {
            // Update fulfillment status in UI
            const updateFulfillmentStatus = (prevOrders: any[]) => {
              return prevOrders.map(o => {
                if (o.orderId === order.orderId) {
                  return {
                    ...o,
                    fulfillment_status: 'fulfilled',
                    fulfillmentStatus: 'fulfilled',
                    fulfilled_at: new Date().toISOString(),
                    shopify_fulfillment_id: fulfillResponse.data.fulfillment?.id,
                    is_shipped: true
                  };
                }
                return o;
              });
            };

            //setParsedOrders(updateFulfillmentStatus);
            //setAllParsedOrders(updateFulfillmentStatus);

            enqueueSnackbar('訂單已自動標記為已出貨！', {
              variant: 'success',
              autoHideDuration: 4000
            });
          }
        } catch (fulfillError: any) {
          console.error('Auto-fulfill error:', fulfillError);
          enqueueSnackbar('物流單已建立，但自動標記出貨失敗。請手動點擊「已出貨」按鈕。', {
            variant: 'warning',
            autoHideDuration: 6000
          });
        }
      } else {
        throw new Error(response.data?.message || '建立物流單失敗');
      }

    } catch (error: any) {
      console.error('Quick ship error:', error);
      enqueueSnackbar('快速出貨失敗: ' + (error.response?.data?.error || error.message), {
        variant: 'error'
      });
    }
  }, [enqueueSnackbar, setParsedOrders, setAllParsedOrders, socket, calculateOrderTotal]);

  const getCurrentPageOrders = () => {
    const indexOfLastOrder = currentPage * ordersPerPage;
    const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
    return parsedOrders.slice(indexOfFirstOrder, indexOfLastOrder);
  };

  // Optimized height estimation
  const estimateHeight = useCallback((order: any) => {
    const baseHeight = 350; // Increased for better spacing
    const perItemHeight = 140; // Increased for responsive table rows
    const extraHeight = order.trackingNumber ? 60 : 0;
    const galleryHeight = order.showGallery ? 450 : 0;
    const alertHeight = order.isFiltered ? 100 : 0;
    const summaryHeight = order.isFiltered ? 80 : 0;

    // Account for action buttons wrapping on small screens
    const actionButtonsHeight = 80; // Add extra height for button wrapping

    // Use actual item count being displayed
    const displayedItemCount = order.items?.length || 1;

    return baseHeight + (perItemHeight * displayedItemCount) + extraHeight +
      galleryHeight + alertHeight + summaryHeight + actionButtonsHeight;
  }, []);


  const parseItemSpec = useCallback((itemSpec: string) => {
    if (!itemSpec) return { color: '', size: '' };

    // Split by comma - first part is color, second part is size
    const parts = itemSpec.split(',');
    const color = parts[0]?.trim() || '';
    const size = parts[1]?.trim() || '';

    return { color, size };
  }, []);

  // Add this function to extract unique colors and sizes from all orders
  const getUniqueColorsAndSizes = useMemo(() => {
    const colors = new Set<string>();
    const sizes = new Set<string>();

    allParsedOrders.forEach(order => {
      order.items.forEach((item: OrderItem) => {
        const { color, size } = parseItemSpec(item.itemSpec);
        if (color) colors.add(color);
        if (size) sizes.add(size);
      });
    });

    return {
      colors: Array.from(colors).sort(),
      sizes: Array.from(sizes).sort()
    };
  }, [allParsedOrders, parseItemSpec]);

  const getItemSize = useCallback((index: number) => {
    return measuredHeightsRef.current[index] ||
      (parsedOrders[index] ? estimateHeight(parsedOrders[index]) : 300);
  }, [parsedOrders, estimateHeight]);

  const getProductKey = useCallback((sku: string, productName: string, itemSpec: string) => {
    if (sku && sku.trim()) {
      return sku.trim();
    }
    return `${productName}|${itemSpec || 'default'}`.toLowerCase().trim();
  }, []);

  const decodeHtmlEntities = useCallback((input: string): string => {
    const textArea = document.createElement('textarea');
    textArea.innerHTML = input;
    return textArea.value;
  }, []);

  const formatShippingAddress = useCallback((address: any) => {
    if (!address) return '';

    const parts = [
      address.address1,
      address.address2,
      address.city,
      address.province,
      address.country,
      address.zip
    ];

    return parts.filter(Boolean).join(', ');
  }, []);

  // Fetch Shopify orders (backend handles caching - returns fast from in-memory cache)
  const fetchShopifyOrdersComplete = useCallback(async (forceRefresh = false) => {
    if (!shopifyAuthenticated) return;

    if (fetchingRef.current) {
      console.log('Fetch already in progress, skipping...');
      return;
    }

    fetchingRef.current = true;

    try {
      if (!forceRefresh) {
        setIsLoadingFromCache(true);
      } else {
        setLoading(true);
      }

      const response = await api.get('/api/shopify/orders', {
        params: {
          limit: 600,
          showHidden: showHiddenOrders,
          fulfillmentFilter: fulfillmentFilter,
          forceRefresh: forceRefresh
        }
      });

      const { orders: shopifyOrders = [], performance } = response.data || {};

      setOrders(shopifyOrders);

      const source = performance?.source || 'unknown';
      if (forceRefresh) {
        enqueueSnackbar(`Loaded ${shopifyOrders.length} orders (refreshed)`, {
          variant: 'success',
          autoHideDuration: 3000
        });
      } else if (source === 'cached') {
        console.log(`Loaded ${shopifyOrders.length} orders from cache in ${performance?.duration}ms`);
      }

    } catch (error) {
      console.error('Failed to fetch Shopify orders:', error);
      enqueueSnackbar('Failed to fetch orders', { variant: 'error' });
    } finally {
      setLoading(false);
      setIsLoadingFromCache(false);
      setBackgroundRefreshing(false);
      fetchingRef.current = false;
    }
  }, [shopifyAuthenticated, enqueueSnackbar, showHiddenOrders, fulfillmentFilter]);

  const enlargeImageUrl = useCallback((imageUrl: string | null) => {
    if (!imageUrl) return null;

    // Extract base URL (before query parameters)
    const baseUrl = imageUrl.split('?')[0];

    // Return larger version
    return `${baseUrl}?width=600&height=600&crop=center`;
  }, []);

  // Add these state variables near your other useState declarations
  const [editingAddress, setEditingAddress] = useState<{ [orderId: string]: boolean }>({});
  const [addressEdits, setAddressEdits] = useState<{ [orderId: string]: string }>({});

  // Add this validation function
  const validateTaiwanAddress = useCallback((address: string): { isValid: boolean; missing: string[] } => {
    if (!address) return { isValid: false, missing: ['地址'] };

    const missing: string[] = [];

    // Check for 市 OR 縣 (they're equivalent for validation purposes)
    const hasCityMarker = /[市縣]/.test(address);
    if (!hasCityMarker) {
      missing.push('市/縣');
    }

    // Check for 區 (district)
    const hasDistrictMarker = /區/.test(address);
    if (!hasDistrictMarker) {
      missing.push('區');
    }

    // Return validation result, but DON'T block editing
    return {
      isValid: missing.length === 0,
      missing
    };
  }, []);

  // Add handler for address editing
  const handleAddressEdit = useCallback((orderId: string, newAddress: string) => {
    setAddressEdits(prev => ({
      ...prev,
      [orderId]: newAddress
    }));
  }, []);

  // Add handler for saving address
  const handleSaveAddress = useCallback(async (order: any) => {
    const newAddress = addressEdits[order.orderId];

    if (!newAddress || newAddress === order.shippingAddress) {
      setEditingAddress(prev => ({ ...prev, [order.orderId]: false }));
      return;
    }

    try {
      enqueueSnackbar('正在更新地址...', { variant: 'info' });

      // Call API to update the address
      const response = await api.put(`/api/shopify/orders/${order.orderNumber}/address`, {
        shippingAddress: newAddress
      });

      if (response.data.success) {
        // Update the order in UI
        const updateOrderAddress = (prevOrders: any[]) => {
          return prevOrders.map(o => {
            if (o.orderId === order.orderId) {
              return {
                ...o,
                shippingAddress: newAddress,
                // Update the raw order data if it exists
                original: {
                  ...o.original,
                  shipping_address: {
                    ...o.original?.shipping_address,
                    address1: newAddress
                  }
                }
              };
            }
            return o;
          });
        };

        setParsedOrders(updateOrderAddress);
        setAllParsedOrders(updateOrderAddress);

        setEditingAddress(prev => ({ ...prev, [order.orderId]: false }));
        setAddressEdits(prev => {
          const newEdits = { ...prev };
          delete newEdits[order.orderId];
          return newEdits;
        });

        enqueueSnackbar('地址更新成功', { variant: 'success' });
      }
    } catch (error) {
      console.error('Error updating address:', error);
      enqueueSnackbar('地址更新失敗', { variant: 'error' });
    }
  }, [addressEdits, enqueueSnackbar, setParsedOrders, setAllParsedOrders]);

  // Add handler for canceling address edit
  const handleCancelAddressEdit = useCallback((orderId: string) => {
    setEditingAddress(prev => ({ ...prev, [orderId]: false }));
    setAddressEdits(prev => {
      const newEdits = { ...prev };
      delete newEdits[orderId];
      return newEdits;
    });
  }, []);

  const HoverZoomImage: React.FC<HoverZoomImageProps> = ({
    src,
    alt,
    width = 60,
    height = 60,
    borderColor = '#e0e0e0'
  }) => {
    const [isHovered, setIsHovered] = useState<boolean>(false);
    const imageRef = useRef<HTMLImageElement>(null);

    const handleMouseEnter = () => {
      setIsHovered(true);
    };

    const handleMouseLeave = () => {
      setIsHovered(false);
    };

    const handleClick = () => {
      const largerImageUrl = src.includes('?')
        ? src.replace(/width=\d+/, 'width=800').replace(/height=\d+/, 'height=800')
        : `${src}?width=800&height=800`;
      window.open(largerImageUrl, '_blank');
    };

    // Create portal element that renders at document.body level
    const renderZoomedImage = () => {
      if (!isHovered) return null;

      return (
        <>
          {/* Full-page backdrop */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
              zIndex: 99998,
              pointerEvents: 'none',
              transition: 'opacity 0.3s ease-in-out'
            }}
          />

          {/* Enlarged image - using document coordinates */}
          <div
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              width: '0px',
              height: '0px',
              zIndex: 99999,
              pointerEvents: 'none'
            }}
          >
            <img
              src={src}
              alt={alt}
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '600px',
                height: '600px',
                objectFit: 'contain',
                backgroundColor: 'white',
                padding: '16px',
                borderRadius: '12px',
                boxShadow: '0 24px 96px rgba(0,0,0,0.7)',
                border: `4px solid ${borderColor}`,
                maxWidth: '90vw',
                maxHeight: '90vh',
                display: 'block'
              }}
            />
          </div>
        </>
      );
    };

    return (
      <>
        <img
          ref={imageRef}
          src={src}
          alt={alt}
          style={{
            width: `${width}px`,
            height: `${height}px`,
            objectFit: 'contain',
            cursor: 'pointer',
            transition: 'opacity 0.2s ease'
          }}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          onClick={handleClick}
          loading="lazy"
        />

        {/* Use createPortal to render outside current DOM tree */}
        {typeof document !== 'undefined' &&
          document.body &&
          require('react-dom').createPortal(
            renderZoomedImage(),
            document.body
          )
        }
      </>
    );
  };

  // Optimized Shopify parsing
  const parseShopifyOrders = useCallback(async () => {
    if (!orders || orders.length === 0) {
      setParsedOrders([]);
      setAllParsedOrders([]);
      return;
    }

    // 1. Fetch tracking data
    let trackingData: any[] = [];
    try {
      const trackingResponse = await api.get('/api/orders/shopify-tracking');
      trackingData = trackingResponse.data || [];
      console.log(`Fetched ${trackingData.length} Shopify tracking records`);
    } catch (error) {
      console.error('Failed to fetch Shopify tracking data:', error);
      trackingData = [];
    }

    // 2. [OPTIMIZATION] Create a Lookup Map (O(1) access)
    // This prevents the browser freeze by avoiding nested loops
    const trackingLookup = new Map<string, any>();

    trackingData.forEach((t: any) => {
      // Handle both camelCase and snake_case to be safe
      const tOrderId = t.orderId || t.order_id || t.orderSn || t.order_sn;
      const tProd = t.productName || t.product_name || '';
      const tVar = t.variantTitle || t.variant_title || t.itemSpec || 'Default';

      if (tOrderId) {
        // Create a unique key: "OrderID_ProductName_Variant"
        const key = `${tOrderId.toString()}_${tProd}_${tVar}`;
        trackingLookup.set(key, t);
      }
    });

    // 3. Process Orders
    const groupedOrders = orders.map((order: any) => {
      const items = order.items || order.line_items || [];

      // Safely get order number
      const orderNumberString = order.order_number ? order.order_number.toString() : (order.orderNumber || order.id || '').toString();

      if (!orderNumberString) {
        return null;
      }

      let displayOrderNumber = `${orderNumberString}`;
      if (order.shopify_store_name === 'mdoc') {
        displayOrderNumber = `mdoc-${orderNumberString}`;
      }

      const orderItems = items.map((item: any, itemIndex: number) => {
        // Safely get item properties
        const productName = item.product_name || item.title || item.name || '';
        const variantTitle = item.variant_title || item.itemSpec || 'Default';
        const itemId = item.shopify_line_item_id || item.id || `${orderNumberString}_item_${itemIndex}`;

        // [OPTIMIZATION] Instant Map Lookup instead of .find()
        const lookupKey = `${orderNumberString}_${productName}_${variantTitle}`;
        const itemTracking = trackingLookup.get(lookupKey);

        const productKey = getProductKey(
          item.sku || '',
          productName,
          variantTitle
        );

        const metadata = productMetadata[productKey] || {};

        return {
          id: `${orderNumberString}_${itemId}`,
          orderId: orderNumberString,
          productName: productName,
          itemSpec: variantTitle,
          quantity: item.quantity || 1,
          imageUrl: item.image_url || '',
          sku: item.sku || '',
          productKey: productKey,
          price: item.price || 0,
          total_discount: item.total_discount || 0,

          // Use lookup data
          received: itemTracking ? itemTracking.received : false,
          missingCount: itemTracking ? itemTracking.missing_count : 0,
          note: itemTracking ? itemTracking.note : '',
          tag: metadata.tag || (itemTracking ? itemTracking.tag : '') || '',
          referenceImage: metadata.reference_image || (itemTracking ? itemTracking.reference_image : ''),

          receivedBy: item.receivedBy || item.received_by || (itemTracking ? itemTracking.received_by : null),
          receivedAt: item.receivedAt || item.received_at || (itemTracking ? itemTracking.received_at : null),

          // Debug info (optional, helps verify lookup worked)
          _debug: {
            hasTracking: !!itemTracking,
            source: itemTracking ? 'map_lookup' : 'none'
          },

          // Keep order-level tracking info
          trackingNumber: order.tracking_number,
          paymentNo: order.payment_no,
          cvsValidationNo: order.cvs_validation_no,
          ecpayLogisticsId: order.ecpay_logistics_id,
          logistics_sub_type: order.logistics_sub_type,
          store_id: order.store_id,
          delivery_method: order.delivery_method || 'pickup',
          product_images: item.product_images || [],
          lineItemId: item.shopify_line_item_id || item.id,
          original: {
            ...item.raw_line_item_data,
            variant_id: item.variant_id || item.raw_line_item_data?.variant_id,
            product_id: item.product_id || item.raw_line_item_data?.product_id,
            id: item.shopify_line_item_id || item.id
          }
        };
      });

      // Calculate received status
      const allReceived = orderItems.every((item: any) => item.received);
      const anyReceived = orderItems.some((item: any) => item.received);
      const receivedItemsCount = orderItems.filter((item: any) => item.received).length;


      // Find first item with tracking info to propagate to order level
      const firstItemWithTracking = orderItems.find((item: any) =>
        item.trackingNumber || item.paymentNo || item.ecpayLogisticsId
      );

      let receivedStatus = 'pending';
      if (allReceived && orderItems.length > 0) receivedStatus = 'complete';
      else if (anyReceived) receivedStatus = 'partial';

      // Safely get customer info
      const customerName = order.customer_name ||
        fixChineseNameOrder(order.customer?.first_name || '', order.customer?.last_name || '') ||
        'Customer';
      const customerPhone = order.customer_phone || getCustomerPhone(order) || '';

      const createdAt = order.created_at ? moment(order.created_at).format('YYYY-MM-DD HH:mm') : moment().format('YYYY-MM-DD HH:mm');
      const createdTimestamp = order.created_at ? new Date(order.created_at).getTime() / 1000 : Date.now() / 1000;

      return {
        orderId: orderNumberString,
        orderNumber: displayOrderNumber,
        displayOrderNumber: displayOrderNumber,
        shopify_store_name: order.shopify_store_name,
        createdAt: createdAt,
        createdTimestamp: createdTimestamp,
        deadline: moment(order.created_at || new Date()).add(3, 'days').format('YYYY-MM-DD HH:mm'),
        fulfillment_status: order.fulfillment_status || 'unfulfilled',
        fulfillmentStatus: order.fulfillment_status || 'unfulfilled',
        status: order.financial_status || 'pending',
        receivedStatus: receivedStatus,
        receivedItemsInOrder: receivedItemsCount,
        totalItemsInOrder: orderItems.length,
        allItemsReceived: allReceived,
        totalItems: orderItems.length,
        customer: customerName,
        customerPhone: customerPhone,
        shippingAddress: formatShippingAddress(order.shipping_address),
        items: orderItems,
        original: order,
        hasTrackingInfo: !!(order.tracking_number || order.payment_no || order.ecpay_logistics_id),

        // Fallback to item tracking info if order level is missing it
        tracking_number: order.tracking_number || firstItemWithTracking?.trackingNumber,
        payment_no: order.payment_no || firstItemWithTracking?.paymentNo,
        cvs_validation_no: order.cvs_validation_no || firstItemWithTracking?.cvsValidationNo,
        ecpay_logistics_id: order.ecpay_logistics_id || firstItemWithTracking?.ecpayLogisticsId,

        is_hidden: Boolean(order.is_hidden),
        logistics_sub_type: order.logistics_sub_type,
        store_id: order.store_id,
        store_name: order.store_name,
        deliveryMethod: order.delivery_method || 'pickup',
        readyToShip: allReceived && (order.financial_status === 'paid')
      };
    }).filter(Boolean);

    setAllParsedOrders(groupedOrders);
  }, [orders, productMetadata, getProductKey, formatShippingAddress, fixChineseNameOrder, getCustomerPhone]);

  const copyToClipboard = useCallback((text: string | undefined) => {
    if (!text) return;

    navigator.clipboard.writeText(text)
      .then(() => {
        enqueueSnackbar('已複製到剪貼簿', { variant: 'success' });
      })
      .catch(err => {
        console.error('Failed to copy text: ', err);
        enqueueSnackbar('複製失敗', { variant: 'error' });
      });
  }, [enqueueSnackbar]);

  const fetchTrackingNumber = useCallback(async (order: any) => {
    if (!order.ecpayLogisticsId || order.trackingNumber) return;

    try {
      enqueueSnackbar('正在獲取追蹤號碼...', { variant: 'info' });

      const response = await api.get(`/api/ecpay/logistics/tracking`, {
        params: {
          logisticsId: order.ecpayLogisticsId,
          orderId: order.orderId
        }
      });

      if (response.data && response.data.success) {
        const trackingUpdate = {
          trackingNumber: response.data.trackingNumber,
          paymentNo: response.data.paymentNo,
          cvsValidationNo: response.data.validationNo,
          ecpayLogisticsId: response.data.logisticsId
        };

        // Update the order in the list
        setParsedOrders(prevOrders => {
          return prevOrders.map(o => {
            if (o.orderId === order.orderId) {
              return { ...o, ...trackingUpdate, hasTrackingInfo: true };
            }
            return o;
          });
        });

        setAllParsedOrders(prevOrders => {
          return prevOrders.map(o => {
            if (o.orderId === order.orderId) {
              return { ...o, ...trackingUpdate, hasTrackingInfo: true };
            }
            return o;
          });
        });

        enqueueSnackbar(`已獲取追蹤號碼: ${response.data.trackingNumber}`, {
          variant: 'success',
          autoHideDuration: 5000
        });
      }
    } catch (error) {
      console.error('Error getting tracking number:', error);
      enqueueSnackbar('獲取追蹤號碼時發生錯誤', { variant: 'error' });
    }
  }, [enqueueSnackbar, setParsedOrders, setAllParsedOrders]);

  // Optimized filtering with memoization
  // In your ShopifyOrdersPage component, update the filteredAndSortedOrders useMemo
  const filteredAndSortedOrders = useMemo(() => {
    if (!allParsedOrders.length) return [];

    let filtered = allParsedOrders;

    if (!showHiddenOrders) {
      filtered = filtered.filter(order => !order.is_hidden);
    }

    // Apply search filter - filter both orders and items within orders
    if (searchFilter) {
      const filter = searchFilter.toLowerCase().trim();

      filtered = allParsedOrders.map(order => {
        // Check if this order is expanded
        const isExpanded = expandedOrders.has(order.orderId);

        // NEW: Check if search matches order ID or order number
        const orderIdMatch =
          order.orderId.toString().toLowerCase().includes(filter) ||
          order.orderNumber.toString().toLowerCase().includes(filter) ||
          `#${order.orderNumber}`.toLowerCase().includes(filter);

        // Check if search matches customer name
        const customerMatch = order.customer && order.customer.toLowerCase().includes(filter);

        // Check if search matches order status
        const statusMatch = order.status && order.status.toLowerCase().includes(filter);

        // Filter items within this order that match the search
        const matchingItems = order.items.filter((item: { itemSpec: string; productName: string; tag: string; sku: string; note: string; }) =>
          (item.itemSpec && item.itemSpec.toLowerCase().includes(filter)) ||
          (item.productName && item.productName.toLowerCase().includes(filter)) ||
          (item.tag && item.tag.toLowerCase().includes(filter)) ||
          (item.sku && item.sku.toLowerCase().includes(filter)) ||
          (item.note && item.note.toLowerCase().includes(filter))
        );

        // If order ID, customer, status matches, OR any items match, return order
        if (orderIdMatch || customerMatch || statusMatch || matchingItems.length > 0) {
          // Check if all items are received
          const allItemsReceived = order.items.every((item: { received: any; }) => item.received);

          // If it's an order-level match (not item-specific), show all items
          const shouldShowAllItems = orderIdMatch || customerMatch || statusMatch;

          return {
            ...order,
            // Show all items if order-level match OR expanded OR if all items are received during search
            items: (shouldShowAllItems || isExpanded || allItemsReceived) ? order.items : matchingItems,
            originalItemCount: order.items.length,
            filteredItemCount: shouldShowAllItems ? order.items.length : matchingItems.length,
            matchingItemCount: matchingItems.length,
            isFiltered: !(shouldShowAllItems || allItemsReceived), // Not filtered if order-level match or all items received
            isExpanded: isExpanded || allItemsReceived || shouldShowAllItems, // Auto-expanded if complete or order-level match
            allItems: order.items,
            allItemsReceived: allItemsReceived,
            // Add flags to show what matched
            matchedOrderId: orderIdMatch,
            matchedCustomer: customerMatch,
            matchedStatus: statusMatch,
            matchedItems: matchingItems.length > 0
          };
        }

        return null; // No matching items or order info
      }).filter(Boolean); // Remove null orders
    } else {
      // No search filter - show all orders with all items
      filtered = allParsedOrders.map(order => ({
        ...order,
        originalItemCount: order.items.length,
        filteredItemCount: order.items.length,
        isFiltered: false,
        allItemsReceived: order.items.every((item: { received: any; }) => item.received)
      }));
    }

    // Rest of your existing filter logic (color, size, status filters)...
    // Apply color filter
    if (fulfillmentFilter !== 'All') {
      filtered = filtered.filter(order => {
        const fulfillmentStatus = order.fulfillment_status || order.fulfillmentStatus || 'unfulfilled';

        switch (fulfillmentFilter) {
          case 'unfulfilled':
            return fulfillmentStatus === 'unfulfilled' || fulfillmentStatus === null;
          case 'fulfilled':
            return fulfillmentStatus === 'fulfilled';
          case 'partially_fulfilled':
            return fulfillmentStatus === 'partial' || fulfillmentStatus === 'partially_fulfilled';
          default:
            return true;
        }
      });


    }

    if (colorFilter !== 'All') {
      filtered = filtered.map(order => {
        const matchingItems = order.items.filter((item: OrderItem) => {
          const { color } = parseItemSpec(item.itemSpec);
          return color === colorFilter;
        });

        if (matchingItems.length > 0) {
          const isExpanded = expandedOrders.has(order.orderId);
          return {
            ...order,
            items: isExpanded ? order.items : matchingItems,
            filteredItemCount: matchingItems.length,
            isFiltered: true,
            colorFiltered: true
          };
        }
        return null;
      }).filter(Boolean);
    }

    // Apply size filter
    if (sizeFilter !== 'All') {
      filtered = filtered.map(order => {
        const matchingItems = order.items.filter((item: OrderItem) => {
          const { size } = parseItemSpec(item.itemSpec);
          return size === sizeFilter;
        });

        if (matchingItems.length > 0) {
          const isExpanded = expandedOrders.has(order.orderId);
          return {
            ...order,
            items: isExpanded ? order.items : matchingItems,
            filteredItemCount: matchingItems.length,
            isFiltered: true,
            sizeFiltered: true
          };
        }
        return null;
      }).filter(Boolean);
    }

    if (tagFilter !== 'All') {
      filtered = filtered.map(order => {
        let matchingItems;

        if (tagFilter === 'NumbersOnly') {
          matchingItems = order.items.filter((item: OrderItem) => hasNumbersOnly(item.tag || ''));
        } else if (tagFilter === 'NoNumbers') {
          matchingItems = order.items.filter((item: OrderItem) => !hasNumbersOnly(item.tag || ''));
        } else if (tagFilter === 'NoTag') {
          matchingItems = order.items.filter((item: OrderItem) => !item.tag || item.tag.trim() === '');
        } else {
          matchingItems = order.items;
        }

        if (matchingItems.length > 0) {
          const isExpanded = expandedOrders.has(order.orderId);
          return {
            ...order,
            items: isExpanded ? order.items : matchingItems,
            filteredItemCount: matchingItems.length,
            isFiltered: true
          };
        }
        return null;
      }).filter(Boolean);
    }

    // Apply status filter
    if (statusFilter !== 'All') {
      filtered = filtered.filter(order => order.status === statusFilter);
    } else {
      // Hide refunded orders by default
      filtered = filtered.filter(order => order.status !== 'refunded');
    }

    if (receivedStatusFilter !== 'All') {
      filtered = filtered.filter(order => {
        // Get the original order to check ALL items (not just filtered ones)
        const originalOrder = allParsedOrders.find(o => o.orderId === order.orderId) || order;
        const allItems = originalOrder.items || [];

        const allReceived = allItems.every((item: { received: any; }) => item.received);

        switch (receivedStatusFilter) {
          case 'ready-to-ship':
            return allReceived;
          case 'complete':
            return allReceived;
          case 'not-received':
            return (order.items || []).some((item: { received: any }) => !item.received)
              || !!justCompletedOrders[order.orderId?.toString()];
          case 'overdue': {
            const orderDate = order.createdAt ? new Date(order.createdAt) : null;
            if (!orderDate) return false;
            const daysSinceCreated = (Date.now() - orderDate.getTime()) / (1000 * 60 * 60 * 24);
            return daysSinceCreated > 10 && !order.hasTrackingInfo;
          }
          default:
            return true;
        }
      });
    }


    // Update received status based on ALL items in order (not just filtered ones)
    filtered = filtered.map(order => {
      const originalOrder = allParsedOrders.find(o => o.orderId === order.orderId);
      if (originalOrder) {
        const allReceived = originalOrder.items.every((item: { received: any; }) => item.received);
        const anyReceived = originalOrder.items.some((item: { received: any; }) => item.received);

        let receivedStatus = 'pending';
        if (allReceived) receivedStatus = 'complete';
        else if (anyReceived) receivedStatus = 'partial';

        // ✅ Calculate readyToShip with proper status check
        const isPaid = order.status === 'paid' ||
          order.status === 'partially_refunded' ||
          order.status === 'refunded';
        const readyToShip = allReceived && isPaid;

        // Auto-expand orders that are ready to ship when "未收到" filter is active
        const shouldAutoExpand = receivedStatusFilter === 'not-received' && readyToShip;

        return {
          ...order,
          items: shouldAutoExpand ? originalOrder.items : order.items,
          originalItemCount: originalOrder.items.length,
          filteredItemCount: shouldAutoExpand ? originalOrder.items.length : order.filteredItemCount,
          isFiltered: shouldAutoExpand ? false : order.isFiltered,
          isExpanded: shouldAutoExpand ? true : order.isExpanded,
          receivedStatus,
          readyToShip, // ✅ Update the flag
          allItemsReceived: allReceived,
          totalItemsInOrder: originalOrder.items.length,
          receivedItemsInOrder: originalOrder.items.filter((item: { received: any; }) => item.received).length
        };
      }
      return order;
    });

    // Apply sort order
    if (sortOrder === 'newest') {
      filtered.sort((a, b) => b.createdTimestamp - a.createdTimestamp);
    } else {
      filtered.sort((a, b) => a.createdTimestamp - b.createdTimestamp);
    }

    return filtered;
  }, [allParsedOrders, searchFilter, statusFilter, sortOrder, expandedOrders, colorFilter, sizeFilter, tagFilter, showHiddenOrders, receivedStatusFilter, fulfillmentFilter, parseItemSpec, hasNumbersOnly, justCompletedOrders]);

  const autoExpandCompleteOrders = useCallback((orderId: string) => {
    setExpandedOrders(prev => {
      const newSet = new Set(prev);
      newSet.add(orderId);
      return newSet;
    });
  }, []);

  const determineDocumentType = useCallback((storeId?: string, logisticsSubType?: string): string => {
    if (logisticsSubType) return logisticsSubType;
    if (storeId) {
      if (storeId.startsWith('F') || /^[A-Z]\d{5}$/.test(storeId)) return 'FAMIC2C';
    }
    return 'UNIMARTC2C';
  }, []);





  // Handle tracking updates
  const handleTrackingUpdate = useCallback((orderItem: OrderItem, field: string, value: any) => {
    const userId = currentUser?.id?.toString() || localStorage.getItem('userId');
    const userName = currentUser?.name || localStorage.getItem('userName') || 'Unknown user';
    const currentTime = new Date().toISOString();

    // OPTIMIZED: Define update function for immediate UI updates
    const updateOrdersOptimistically = (prevOrders: any[]) => {
      return prevOrders.map(order => {
        if (order.orderId !== orderItem.orderId) return order;

        const updatedItems = order.items.map((item: OrderItem) => {
          if (item.id !== orderItem.id) return item;

          // Handle received field with user tracking
          if (field === 'received') {
            return {
              ...item,
              received: value,
              ...(value ? {
                receivedBy: userName,
                receivedAt: currentTime
              } : {
                receivedBy: undefined,
                receivedAt: undefined
              })
            };
          }

          // Handle other fields
          return { ...item, [field]: value };
        });

        // Recalculate order status
        const allReceived = updatedItems.every((i: { received: any; }) => i.received);
        const anyReceived = updatedItems.some((i: { received: any; }) => i.received);
        const receivedItemsCount = updatedItems.filter((i: { received: any; }) => i.received).length;
        const readyToShip = allReceived;

        // Check if order has tracking info
        const hasTrackingInfo = updatedItems.some((item: OrderItem) =>
          item.trackingNumber || item.paymentNo || item.ecpayLogisticsId
        );

        const firstItemWithTracking = updatedItems.find((item: OrderItem) =>
          item.trackingNumber || item.paymentNo || item.ecpayLogisticsId
        );

        return {
          ...order,
          items: updatedItems,
          receivedStatus: allReceived ? 'complete' : anyReceived ? 'partial' : 'pending',
          receivedItemsInOrder: receivedItemsCount,
          totalItemsInOrder: updatedItems.length,
          allItemsReceived: allReceived,
          hasTrackingInfo: hasTrackingInfo,
          tracking_number: firstItemWithTracking?.trackingNumber || order.tracking_number,
          payment_no: firstItemWithTracking?.paymentNo || order.payment_no,
          cvs_validation_no: firstItemWithTracking?.cvsValidationNo || order.cvs_validation_no,
          ecpay_logistics_id: firstItemWithTracking?.ecpayLogisticsId || order.ecpay_logistics_id,
          readyToShip: readyToShip
        };
      });
    };

    // STEP 1: Apply optimistic update IMMEDIATELY (don't wait for server)
    requestAnimationFrame(() => {
      setParsedOrders(updateOrdersOptimistically);
      setAllParsedOrders(updateOrdersOptimistically);
    });

    // If marking received while 未收到 filter is active, track orders that just became fully received
    if (field === 'received' && receivedStatusFilter === 'not-received') {
      const currentOrder = allParsedOrders.find(o => o.orderId === orderItem.orderId);
      if (currentOrder) {
        const wasFullyReceived = currentOrder.items.every((i: OrderItem) => i.received);
        const updatedItems = currentOrder.items.map((i: OrderItem) =>
          i.id === orderItem.id ? { ...i, received: value } : i
        );
        const willBeFullyReceived = updatedItems.every((i: OrderItem) => i.received);
        if (!wasFullyReceived && willBeFullyReceived) {
          setJustCompletedOrders(prev => ({ ...prev, [orderItem.orderId.toString()]: true }));
        } else if (!value) {
          setJustCompletedOrders(prev => { const next = { ...prev }; delete next[orderItem.orderId.toString()]; return next; });
        }
      }
    }

    // STEP 2: Handle tracking-related fields (tracking number, payment no, etc.)
    if (['trackingNumber', 'paymentNo', 'cvsValidationNo', 'ecpayLogisticsId'].includes(field)) {
      // Fire-and-forget API call
      const updateData = {
        orderId: orderItem.orderId.toString(),
        productName: orderItem.productName,
        variantTitle: orderItem.itemSpec,
        [field]: value,
        userId: userId,
        userName: userName,
        timestamp: currentTime
      };

      api.post('/api/orders/shopify-tracking', updateData)
        .then(() => {
          console.log(`${field} updated successfully`);
          enqueueSnackbar(`${field} updated`, { variant: 'success', autoHideDuration: 2000 });
        })
        .catch(error => {
          console.error(`Failed to update ${field}:`, error);
          enqueueSnackbar(`Failed to update ${field}`, { variant: 'error' });
        });

      // Emit socket update
      if (socket && socket.connected) {
        socket.emit('tracking-update', {
          shop: 'shopify',
          orderId: orderItem.orderId,
          productName: orderItem.productName,
          itemSpec: orderItem.itemSpec || '',
          changes: { [field]: value },
          updatedBy: userName,
          userId: userId,
          timestamp: currentTime
        });
      }

      return;
    }

    // STEP 3: Handle tag updates (product metadata)
    if (field === 'tag') {
      const productKey = orderItem.productKey ||
        getProductKey(orderItem.sku, orderItem.productName, orderItem.itemSpec);

      console.log(`Updating tag for product key: ${productKey} to "${value}"`);

      // Fire-and-forget metadata API call
      api.post('/api/products/metadata', {
        productKey,
        tag: value
      })
        .then(response => {
          console.log('Tag updated successfully:', response.data);

          // Update product metadata cache
          setProductMetadata(prev => ({
            ...prev,
            [productKey]: {
              ...prev[productKey],
              product_key: productKey,
              tag: value,
              lastUpdated: new Date().toISOString(),
              createdAt: prev[productKey]?.createdAt || new Date().toISOString(),
              reference_image: prev[productKey]?.reference_image
            }
          }));

          enqueueSnackbar('Tag updated', { variant: 'success', autoHideDuration: 2000 });
        })
        .catch(error => {
          console.error('Failed to update tag:', error);
          enqueueSnackbar('Failed to update tag', { variant: 'error' });
        });

      // Emit socket update for tag
      if (socket && socket.connected) {
        socket.emit('tracking-update', {
          shop: 'shopify',
          orderId: orderItem.orderId,
          productName: orderItem.productName,
          itemSpec: orderItem.itemSpec || '',
          changes: { tag: value },
          productKey: productKey,
          updatedBy: userName,
          userId: userId,
          timestamp: currentTime
        });
      }

      return;
    }

    // STEP 4: Handle other tracking updates (received, missingCount, note)
    const changes: { [key: string]: any } = { [field]: value };

    if (field === 'received' && value === true) {
      changes.receivedBy = userName;
      changes.receivedAt = currentTime;
    }

    // Prepare update data
    const updateData = {
      orderId: orderItem.orderId.toString(),
      productName: orderItem.productName,
      variantTitle: orderItem.itemSpec,
      received: field === 'received' ? value : orderItem.received,
      missingCount: field === 'missingCount' ? value : orderItem.missingCount,
      note: field === 'note' ? value : orderItem.note,
      tag: field === 'tag' ? value : orderItem.tag,
      userId: userId,
      userName: userName
    };

    // Fire-and-forget API call
    api.post('/api/orders/shopify-tracking', updateData)
      .then(() => {
        console.log(`${field} updated successfully`);
      })
      .catch(error => {
        console.error(`Failed to update ${field}:`, error);
        enqueueSnackbar(`Failed to update ${field}`, { variant: 'error' });
      });

    // Emit socket update immediately
    if (socket && socket.connected) {
      socket.emit('tracking-update', {
        shop: 'shopify',
        orderId: orderItem.orderId,
        productName: orderItem.productName,
        itemSpec: orderItem.itemSpec || '',
        changes: changes,
        updatedBy: userName,
        userId: userId,
        timestamp: currentTime
      });
    }

    // STEP 5: Track employee action for received items
    if (field === 'received' && value === true) {
      try {
        const trackingKey = `${orderItem.id}_${new Date().toISOString().split('T')[0]}`;
        if (!window.__trackedItems) {
          window.__trackedItems = new Set();
        }

        if (!window.__trackedItems.has(trackingKey)) {
          window.__trackedItems.add(trackingKey);

          // Lazy load employee tracking service
          import('../services/employeeTrackingService').then(module => {
            const trackingService = module.default;
            trackingService.trackItemReceived(
              orderItem.orderId,
              orderItem.id,
              1,
              {
                productName: orderItem.productName,
                itemSpec: orderItem.itemSpec,
                sku: orderItem.sku,
                user_id: userId,
                user_name: userName
              }
            );
          }).catch(err => {
            console.error('Error loading employee tracking service:', err);
          });
        }
      } catch (err) {
        console.error('Error during tracking:', err);
      }
    }
  }, [
    currentUser,
    getProductKey,
    setParsedOrders,
    setAllParsedOrders,
    setProductMetadata,
    enqueueSnackbar,
    socket,
    receivedStatusFilter,
    allParsedOrders
  ]);

  // Handle variant/size updates (e.g. change L to XL)
  const handleVariantUpdate = useCallback(async (item: OrderItem, newVariant: string) => {
    const trimmed = newVariant.trim();
    if (!trimmed || trimmed === item.itemSpec) return;

    const lineItemId = item.lineItemId || item.original?.id;
    if (!lineItemId) {
      enqueueSnackbar('Cannot update variant: Missing line item ID', { variant: 'error' });
      return;
    }

    // Optimistic update
    const updateOrders = (prev: any[]) => prev.map(order => {
      if (order.orderId !== item.orderId) return order;
      return {
        ...order,
        items: order.items.map((i: OrderItem) =>
          i.id === item.id ? { ...i, itemSpec: trimmed } : i
        ),
      };
    });
    setParsedOrders(updateOrders);
    setAllParsedOrders(updateOrders);

    try {
      await api.patch(`/api/shopify/order-items/${lineItemId}/variant`, { variant_title: trimmed });
      enqueueSnackbar(`規格已更新: ${trimmed}`, { variant: 'success', autoHideDuration: 2000 });
    } catch (error: any) {
      console.error('Failed to update variant:', error);
      enqueueSnackbar(`更新規格失敗: ${error.message}`, { variant: 'error' });
      // Revert
      const revertOrders = (prev: any[]) => prev.map(order => {
        if (order.orderId !== item.orderId) return order;
        return {
          ...order,
          items: order.items.map((i: OrderItem) =>
            i.id === item.id ? { ...i, itemSpec: item.itemSpec } : i
          ),
        };
      });
      setParsedOrders(revertOrders);
      setAllParsedOrders(revertOrders);
    }
  }, [enqueueSnackbar, setParsedOrders, setAllParsedOrders]);

  // Handle SKU updates
  const handleSkuUpdate = useCallback(async (item: OrderItem, newSku: string) => {
    if (!item.original || !item.original.variant_id) {
      enqueueSnackbar('Cannot update SKU: Missing variant ID', { variant: 'error' });
      return;
    }

    try {
      if (!isEditing) {
        scrollPositionRef.current = window.scrollY;
        setIsEditing(true);
      }

      enqueueSnackbar('Updating SKU for all variants...', { variant: 'info', autoHideDuration: 2000 });

      // ✅ Step 1: Update in Shopify
      const response = await api.post('/api/shopify/update-product-variants-sku', {
        variantId: item.original.variant_id,
        newSku
      });

      const productId = item.original.product_id;
      const updatedVariantIds = response.data.updatedVariants || [];

      // ✅ Step 2: Update in your database
      try {
        await api.post('/api/shopify/update-sku-in-database', {
          productId: productId,
          variantIds: updatedVariantIds,
          newSku: newSku
        });
        console.log('✅ SKU updated in database');
      } catch (dbError) {
        console.error('Failed to update SKU in database:', dbError);
        // Don't fail the whole operation if DB update fails
      }

      // ✅ Step 3: Update UI state
      setParsedOrders(prevOrders => {
        return prevOrders.map(order => {
          const updatedItems = order.items.map((orderItem: OrderItem) => {
            const isMatchingVariant = orderItem.original &&
              (orderItem.original.product_id === productId ||
                updatedVariantIds.includes(orderItem.original.variant_id));

            if (isMatchingVariant) {
              return { ...orderItem, sku: newSku };
            }
            return orderItem;
          });

          return { ...order, items: updatedItems };
        });
      });

      // Also update allParsedOrders
      setAllParsedOrders(prevOrders => {
        return prevOrders.map(order => {
          const updatedItems = order.items.map((orderItem: OrderItem) => {
            const isMatchingVariant = orderItem.original &&
              (orderItem.original.product_id === productId ||
                updatedVariantIds.includes(orderItem.original.variant_id));

            if (isMatchingVariant) {
              return { ...orderItem, sku: newSku };
            }
            return orderItem;
          });

          return { ...order, items: updatedItems };
        });
      });

      enqueueSnackbar(`SKU updated to ${newSku} for all variants of this product`, { variant: 'success' });
    } catch (error: any) {
      console.error('Failed to update SKU:', error);
      enqueueSnackbar(`Failed to update SKU: ${error.response?.data?.message || error.message}`, { variant: 'error' });
    } finally {
      if (isEditing) {
        setTimeout(() => {
          setIsEditing(false);
          window.scrollTo(0, scrollPositionRef.current);
        }, 100);
      }
    }
  }, [isEditing, enqueueSnackbar, setParsedOrders, setAllParsedOrders]);

  // Reference image upload
  const handleReferenceImageUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>, item: OrderItem) => {
    const file = event.target.files?.[0];
    if (!file) return;

    enqueueSnackbar('Processing image...', { variant: 'info' });

    const img = new Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      img.src = e.target?.result as string;

      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          const MAX_WIDTH = 600;
          const MAX_HEIGHT = 600;

          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > MAX_WIDTH) {
              height *= MAX_WIDTH / width;
              width = MAX_WIDTH;
            }
          } else {
            if (height > MAX_HEIGHT) {
              width *= MAX_HEIGHT / height;
              height = MAX_HEIGHT;
            }
          }

          canvas.width = width;
          canvas.height = height;
          ctx?.drawImage(img, 0, 0, width, height);

          const compressedBase64 = canvas.toDataURL('image/jpeg', 0.6);
          const productKey = item.productKey || getProductKey(item.sku, item.productName, item.itemSpec);

          console.log(`Uploading reference image for product key: ${productKey}`);

          api.post('/api/products/reference-image/base64', {
            productKey,
            referenceImage: compressedBase64
          })
            .then(response => {
              console.log('Reference image upload response:', response.data);

              setProductMetadata(prev => ({
                ...prev,
                [productKey]: {
                  ...prev[productKey],
                  product_key: productKey,
                  reference_image: compressedBase64,
                  lastUpdated: new Date().toISOString(),
                  createdAt: prev[productKey]?.createdAt || new Date().toISOString(),
                  tag: prev[productKey]?.tag || ''
                }
              }));

              setParsedOrders(prevOrders => {
                return prevOrders.map(order => {
                  const updatedItems = order.items.map((orderItem: OrderItem) => {
                    const itemProductKey = orderItem.productKey ||
                      getProductKey(orderItem.sku, orderItem.productName, orderItem.itemSpec);

                    if (itemProductKey === productKey) {
                      return { ...orderItem, referenceImage: compressedBase64 };
                    }
                    return orderItem;
                  });

                  return { ...order, items: updatedItems };
                });
              });

              enqueueSnackbar('Reference image uploaded successfully', { variant: 'success' });
            })
            .catch(error => {
              console.error('Failed to update reference image:', error);
              enqueueSnackbar('Failed to update reference image: ' + (error.response?.data?.error || error.message), { variant: 'error' });
            });
        } catch (error) {
          console.error('Error processing image:', error);
          enqueueSnackbar('Error processing image', { variant: 'error' });
        }
      };
    };

    reader.readAsDataURL(file);
  }, [getProductKey, setProductMetadata, setParsedOrders, enqueueSnackbar]);

  const handleRemoveReferenceImage = useCallback((item: OrderItem) => {
    const productKey = item.productKey || getProductKey(item.sku, item.productName, item.itemSpec);

    console.log(`Removing reference image for product key: ${productKey}`);

    api.delete(`/api/products/reference-image/${productKey}`)
      .then(response => {
        console.log('Reference image removal response:', response.data);

        setProductMetadata(prev => ({
          ...prev,
          [productKey]: {
            ...prev[productKey],
            reference_image: undefined,
            lastUpdated: new Date().toISOString()
          }
        }));

        setParsedOrders(prevOrders => {
          return prevOrders.map(order => {
            const updatedItems = order.items.map((orderItem: OrderItem) => {
              const itemProductKey = orderItem.productKey ||
                getProductKey(orderItem.sku, orderItem.productName, orderItem.itemSpec);

              if (itemProductKey === productKey) {
                return { ...orderItem, referenceImage: undefined };
              }
              return orderItem;
            });

            return { ...order, items: updatedItems };
          });
        });

        enqueueSnackbar('Reference image removed successfully', { variant: 'success' });
      })
      .catch(error => {
        console.error('Failed to remove reference image:', error);
        enqueueSnackbar('Failed to remove reference image: ' + (error.response?.data?.error || error.message), { variant: 'error' });
      });
  }, [getProductKey, setProductMetadata, setParsedOrders, enqueueSnackbar]);


  const getLogisticsTypeLabel = useCallback((order: any): { label: string, color: 'primary' | 'secondary' | 'success' | 'warning' | 'info' } => {
    // Check shipping address for convenience store keywords
    const shippingAddress = order.shippingAddress || '';
    const storeId = order.store_id || '';
    const logisticsSubType = order.logistics_sub_type || '';

    // Priority 1: Check logistics_sub_type first (most reliable)
    if (logisticsSubType === 'FAMIC2C') {
      return { label: '全家', color: 'success' };
    }
    if (logisticsSubType === 'UNIMARTC2C') {
      return { label: '7-ELEVEN', color: 'primary' };
    }

    // Priority 2: Check store_id pattern
    if (storeId) {
      // Family Mart store IDs typically start with 'F' or match pattern like A12345
      if (storeId.startsWith('F') || /^[A-Z]\d{5}$/.test(storeId)) {
        return { label: '全家', color: 'success' };
      }
      // 7-ELEVEN store IDs are typically numeric
      if (/^\d+$/.test(storeId)) {
        return { label: '7-ELEVEN', color: 'primary' };
      }
    }

    // Priority 3: Check address content for convenience store keywords
    const addressLower = shippingAddress.toLowerCase();

    // Check for Family Mart keywords
    if (addressLower.includes('全家') ||
      addressLower.includes('familymart') ||
      addressLower.includes('family mart') ||
      addressLower.includes('fm')) {
      return { label: '全家', color: 'success' };
    }

    // Check for 7-ELEVEN keywords
    if (addressLower.includes('7-eleven') ||
      addressLower.includes('7-11') ||
      addressLower.includes('711') ||
      addressLower.includes('seven') ||
      addressLower.includes('unimart')) {
      return { label: '7-ELEVEN', color: 'primary' };
    }

    // Check for general convenience store keywords
    if (addressLower.includes('超商') ||
      addressLower.includes('便利商店') ||
      addressLower.includes('convenience store')) {
      // If we can't determine specific type but it's clearly a convenience store
      return { label: '超商取貨', color: 'info' };
    }

    // Default: Home delivery
    return { label: '宅配', color: 'warning' };
  }, []);

  // Add this component for the logistics chip
  const LogisticsTypeChip = memo(({ order }: { order: any }) => {
    const { label, color } = getLogisticsTypeLabel(order);

    const getIcon = () => {
      switch (label) {
        case '7-ELEVEN':
          return <LocalShippingIcon />;
        case '全家':
          return <LocalShippingIcon />;
        case '超商取貨':
          return <LocalShippingIcon />;
        case '宅配':
          return <LocalShipping />;
        default:
          return <LocalShippingIcon />;
      }
    };

    return (
      <Chip
        icon={getIcon()}
        label={label}
        color={color}
        size="small"
        variant="outlined"
        sx={{
          fontWeight: 'bold',
          minWidth: 80
        }}
      />
    );
  });

  const handleFulfillOrder = useCallback(async (order: any) => {
    try {
      enqueueSnackbar('Processing fulfillment request...', {
        variant: 'info',
        autoHideDuration: 2000
      });

      // Check if this is an exchange order
      const isExchange = isExchangeOrder(order.orderNumber);

      if (isExchange) {
        // Exchange order - fulfill locally without calling Shopify API
        enqueueSnackbar('換貨訂單 - 本地標記為已出貨', { variant: 'info' });

        // Update the order in both parsed orders arrays (local only)
        try {
          // First, mark the order as hidden in the database
          const hideResponse = await api.put(`/api/shopify/orders/${order.orderNumber}/hidden`, {
            hidden: true
          });

          if (hideResponse.data.success) {
            // Then update the order in UI - REMOVE it from the list since we're not showing hidden orders
            const removeOrderFromArray = (prevOrders: any[]) => {
              return prevOrders.filter(o => o.orderId !== order.orderId);
            };

            setParsedOrders(removeOrderFromArray);
            setAllParsedOrders(removeOrderFromArray);

            enqueueSnackbar('換貨訂單已標記為已出貨並隱藏', {
              variant: 'success',
              autoHideDuration: 4000
            });
          } else {
            throw new Error('Failed to hide order');
          }
        } catch (hideError) {
          console.error('Error hiding exchange order:', hideError);
          enqueueSnackbar('無法隱藏訂單', { variant: 'error' });
        }

        return; // Exit early - don't call Shopify API
      }

      // Regular Shopify order - proceed with normal fulfillment
      // Determine the best tracking number to use
      let trackingNumber = '';
      let trackingCompany = 'ECPay Logistics';

      if (order.tracking_number) {
        trackingNumber = order.tracking_number;
        trackingCompany = 'ECPay Logistics';
      } else if (order.payment_no) {
        // Use the same logic as the display to determine tracking number
        const logisticsType = order.logistics_sub_type ||
          (order.store_id && (order.store_id.startsWith('F') || /^[A-Z]\d{5}$/.test(order.store_id))
            ? 'FAMIC2C' : 'UNIMARTC2C');

        if (logisticsType === 'FAMIC2C') {
          trackingNumber = order.payment_no;
          trackingCompany = 'Family Mart';
        } else {
          trackingNumber = order.cvs_validation_no
            ? `${order.payment_no}${order.cvs_validation_no}`
            : order.payment_no;
          trackingCompany = '7-ELEVEN';
        }
      }

      console.log('Fulfilling order with tracking info:', {
        orderId: order.orderNumber,
        trackingNumber,
        trackingCompany,
        hasLogisticsInfo: order.hasTrackingInfo
      });

      // Show confirmation if no tracking number
      if (!trackingNumber) {
        const confirmed = window.confirm(
          'No tracking number available for this order. Continue with fulfillment anyway?'
        );
        if (!confirmed) {
          return;
        }
      }

      const response = await api.post(`/api/shopify/orders/${order.orderNumber}/fulfill`, {
        trackingNumber,
        trackingCompany
      });

      if (response.data && response.data.success) {
        // Update the order in both parsed orders arrays
        const updateOrderInArray = (prevOrders: any[]) => {
          return prevOrders.map(o => {
            if (o.orderId === order.orderId) {
              return {
                ...o,
                fulfillment_status: 'fulfilled',
                fulfilled_at: new Date().toISOString(),
                shopify_fulfillment_id: response.data.fulfillment?.id,
                is_shipped: true,
                finalTrackingNumber: response.data.trackingNumber,
                finalTrackingCompany: response.data.trackingCompany
              };
            }
            return o;
          });
        };

        setParsedOrders(updateOrderInArray);
        setAllParsedOrders(updateOrderInArray);

        enqueueSnackbar(
          `Order #${order.orderNumber} fulfilled successfully!${trackingNumber ? ` Tracking: ${trackingNumber}` : ''}`,
          {
            variant: 'success',
            autoHideDuration: 6000
          }
        );

        // Track fulfillment action for analytics
        const userId = currentUser?.id?.toString() || localStorage.getItem('userId');
        const userName = currentUser?.name || localStorage.getItem('userName');

        try {
          import('../services/employeeTrackingService').then(module => {
            const trackingService = module.default;
            trackingService.trackOrderShipped(
              order.orderId,
              order.items ? order.items.length : 0,
              {
                platform: 'shopify',
                trackingNumber: response.data.trackingNumber,
                trackingCompany: response.data.trackingCompany,
                fulfillmentId: response.data.fulfillment?.id,
                user_id: userId,
                user_name: userName
              }
            );
          });
        } catch (trackingError) {
          console.error('Error tracking fulfillment:', trackingError);
        }

      } else {
        throw new Error(response.data?.message || 'Fulfillment failed');
      }
    } catch (error: any) {
      console.error('Fulfillment error:', error);

      let errorMessage = 'Failed to fulfill order';

      if (error.response?.status === 404) {
        errorMessage = 'Order not found';
      } else if (error.response?.status === 409) {
        errorMessage = 'Order is already fulfilled';
      } else if (error.response?.status === 400) {
        errorMessage = error.response?.data?.message || 'Order cannot be fulfilled in its current state';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      // Show more user-friendly error messages
      if (errorMessage.includes('already fulfilled')) {
        enqueueSnackbar('This order has already been fulfilled', { variant: 'warning' });
      } else if (errorMessage.includes('not found')) {
        enqueueSnackbar('Order not found in Shopify', { variant: 'error' });
      } else if (errorMessage.includes('financial_status')) {
        enqueueSnackbar('Order payment must be completed before fulfillment', { variant: 'warning' });
      } else {
        enqueueSnackbar(`Error fulfilling order: ${errorMessage}`, {
          variant: 'error',
          autoHideDuration: 8000
        });
      }
    }
  }, [enqueueSnackbar, setParsedOrders, setAllParsedOrders, isExchangeOrder]);

  // Search handlers
  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    // Update input immediately for user feedback
    setSearchInput(value);

    // Show pending state if there's a difference
    if (value !== searchFilter) {
      setIsSearchPending(true);
    }

    // Trigger debounced search
    debouncedSearch(value);
  }, [debouncedSearch, searchFilter]);

  const clearAllFilters = useCallback(() => {
    setSearchInput('');
    setSearchFilter('');
    setColorFilter('All');
    setSizeFilter('All');
    setTagFilter('All');
    setStatusFilter('All');
    setIsSearchPending(false);
    setJustCompletedOrders({});
    debouncedSearch.cancel();
  }, [debouncedSearch]);

  const clearSearch = useCallback(() => {
    setSearchInput('');
    setSearchFilter('');
    setIsSearchPending(false);
    debouncedSearch.cancel();
  }, [debouncedSearch]);

  const triggerSearch = useCallback(() => {
    debouncedSearch.cancel();
    setSearchFilter(searchInput);
    setIsSearchPending(false);
  }, [searchInput, debouncedSearch]);

  // Effects
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  useEffect(() => {
    const handleResize = _.debounce(() => {
      if (listRef.current) {
        // Clear all cached heights
        measuredHeightsRef.current = {};
        // Force recalculation
        listRef.current.resetAfterIndex(0, true);
      }
    }, 300);

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      handleResize.cancel();
    };
  }, []);

  useEffect(() => {
    if (onOrdersUpdate && allParsedOrders.length > 0) {
      onOrdersUpdate(allParsedOrders);
    }
  }, [allParsedOrders, onOrdersUpdate]);

  useEffect(() => {
    setParsedOrders(filteredAndSortedOrders);
  }, [filteredAndSortedOrders]);

  useEffect(() => {
    // Only clear height cache when expansion state changes, not on every parsedOrders change
    if (listRef.current) {
      Object.keys(measuredHeightsRef.current).forEach(key => {
        const index = parseInt(key);
        if (parsedOrders[index]) {
          delete measuredHeightsRef.current[index];
        }
      });
      // Don't force scroll to top - let virtual list maintain position
      listRef.current.resetAfterIndex(0, false);
    }
  }, [expandedOrders]);

  useEffect(() => {
    // Only reset to page 1 when filters change
    setCurrentPage(1);
  }, [searchFilter, statusFilter, colorFilter, sizeFilter, tagFilter, sortOrder, receivedStatusFilter]);

  useEffect(() => {
    // Clear height cache when search filter changes or page changes
    if (listRef.current) {
      measuredHeightsRef.current = {}; // Clear all cached heights
      listRef.current.resetAfterIndex(0); // Reset virtual list from beginning
    }
  }, [searchFilter, currentPage, colorFilter, sizeFilter]);

  // Fetch orders when filter changes - backend handles caching per filter
  useEffect(() => {
    if (!shopifyAuthenticated) return;
    setCurrentPage(1);
    fetchShopifyOrdersComplete(false);
  }, [shopifyAuthenticated, fulfillmentFilter]);

  // Parse orders when data changes
  useEffect(() => {
    const parseData = async () => {
      if (orders.length > 0) {
        console.log('Raw orders sample before parsing:', {
          order: orders[0],
          hasFulfillmentStatus: !!orders[0]?.fulfillment_status,
          allKeys: Object.keys(orders[0] || {})
        });
        await parseShopifyOrders();
      }
    };

    parseData();
  }, [orders, parseShopifyOrders]);


  // Manual refresh - force sync from Shopify API
  const handleRefresh = useCallback(async () => {
    if (fetchingRef.current) return;
    await fetchShopifyOrdersComplete(true);
  }, [fetchShopifyOrdersComplete]);

  const isTrackingRequired = useCallback((order: any) => {
    const { label } = getLogisticsTypeLabel(order);
    // 宅配 orders don't require tracking numbers for fulfillment
    return label !== '宅配';
  }, [getLogisticsTypeLabel]);


  // Add this useEffect hook to your ShopifyOrdersPage component to listen for socket events
  // Place this after your other useEffect hooks, before the return statement

  useEffect(() => {
    if (!socket) return;

    console.log('Setting up Shopify socket listeners');

    // OPTIMIZED: Tracking update handler with batching
    const handleTrackingUpdate = (data: {
      shop: string;
      orderId: string;
      productName: string;
      itemSpec: string;
      changes: any;
      updatedBy: string;
      userId: string;
      timestamp: string;
      productKey?: string;
      socketId?: string;
    }) => {
      // Only process Shopify updates
      if (data.shop !== 'shopify') return;

      // CRITICAL: Prevent feedback loop by checking socket ID
      if (socket && data.socketId === socket.id) {
        console.log('Ignoring own socket update to prevent race conditions');
        return;
      }

      console.log('Received socket tracking update:', data);

      // OPTIMIZED: Single batch update function
      const updateOrdersWithTracking = (prevOrders: any[]) => {
        return prevOrders.map(order => {
          if (order.orderId.toString() !== data.orderId.toString()) return order;

          let hasChanges = false;
          const updatedItems = order.items.map((item: OrderItem) => {
            // Match by product name and item spec
            if (item.productName === data.productName &&
              (item.itemSpec === data.itemSpec || (!item.itemSpec && !data.itemSpec))) {

              hasChanges = true;

              // Apply all changes in one object spread
              return {
                ...item,
                ...(data.changes.received !== undefined && {
                  received: data.changes.received,
                  ...(data.changes.received ? {
                    receivedBy: data.updatedBy,
                    receivedAt: data.timestamp
                  } : {
                    receivedBy: undefined,
                    receivedAt: undefined
                  })
                }),
                ...(data.changes.trackingNumber !== undefined && { trackingNumber: data.changes.trackingNumber }),
                ...(data.changes.paymentNo !== undefined && { paymentNo: data.changes.paymentNo }),
                ...(data.changes.cvsValidationNo !== undefined && { cvsValidationNo: data.changes.cvsValidationNo }),
                ...(data.changes.ecpayLogisticsId !== undefined && { ecpayLogisticsId: data.changes.ecpayLogisticsId }),
                ...(data.changes.missingCount !== undefined && { missingCount: data.changes.missingCount }),
                ...(data.changes.note !== undefined && { note: data.changes.note }),
                ...(data.changes.tag !== undefined && { tag: data.changes.tag }),
                ...(data.changes.referenceImage !== undefined && { referenceImage: data.changes.referenceImage })
              };
            }

            // Special handling for tag updates via productKey
            if (data.productKey && data.changes.tag !== undefined) {
              const itemProductKey = item.productKey ||
                getProductKey(item.sku, item.productName, item.itemSpec);

              if (itemProductKey === data.productKey) {
                hasChanges = true;
                return { ...item, tag: data.changes.tag };
              }
            }

            return item;
          });

          // Only update if there were actual changes
          if (!hasChanges) return order;

          // Recalculate order-level status efficiently
          const allReceived = updatedItems.every((item: OrderItem) => item.received);
          const anyReceived = updatedItems.some((item: OrderItem) => item.received);
          const receivedItemsCount = updatedItems.filter((item: OrderItem) => item.received).length;
          const hasTrackingInfo = updatedItems.some((item: OrderItem) =>
            item.trackingNumber || item.paymentNo || item.ecpayLogisticsId
          );
          const firstItemWithTracking = updatedItems.find((item: OrderItem) =>
            item.trackingNumber || item.paymentNo || item.ecpayLogisticsId
          );

          return {
            ...order,
            items: updatedItems,
            receivedStatus: allReceived ? 'complete' : anyReceived ? 'partial' : 'pending',
            receivedItemsInOrder: receivedItemsCount,
            totalItemsInOrder: updatedItems.length,
            allItemsReceived: allReceived,
            hasTrackingInfo: hasTrackingInfo,
            tracking_number: firstItemWithTracking?.trackingNumber || order.tracking_number,
            payment_no: firstItemWithTracking?.paymentNo || order.payment_no,
            cvs_validation_no: firstItemWithTracking?.cvsValidationNo || order.cvs_validation_no,
            ecpay_logistics_id: firstItemWithTracking?.ecpayLogisticsId || order.ecpay_logistics_id,
            readyToShip: allReceived && (order.status === 'paid')
          };
        });
      };

      // OPTIMIZED: Single batch update using requestAnimationFrame
      requestAnimationFrame(() => {
        setParsedOrders(updateOrdersWithTracking);
        setAllParsedOrders(updateOrdersWithTracking);
      });

      // Update product metadata if tag was changed
      if (data.productKey && data.changes.tag !== undefined) {
        // Defer metadata update to avoid blocking UI
        requestAnimationFrame(() => {
          setProductMetadata(prev => ({
            ...prev,
            [data.productKey!]: {
              ...prev[data.productKey!],
              product_key: data.productKey!,
              tag: data.changes.tag,
              lastUpdated: data.timestamp,
              createdAt: prev[data.productKey!]?.createdAt || data.timestamp,
              reference_image: prev[data.productKey!]?.reference_image
            }
          }));
        });
      }

      // Show notification
      enqueueSnackbar(
        `Order #${data.orderId} updated by ${data.updatedBy}`,
        {
          variant: 'info',
          autoHideDuration: 3000
        }
      );
    };

    // OPTIMIZED: Order fulfilled handler
    const handleOrderFulfilled = (data: {
      shop: string;
      orderId: string;
      fulfillmentId: string;
      trackingNumber?: string;
      trackingCompany?: string;
      updatedBy: string;
      timestamp: string;
    }) => {
      if (data.shop !== 'shopify') return;

      console.log('Received order fulfillment update:', data);

      // OPTIMIZED: Single update function
      const updateOrderInArray = (prevOrders: any[]) => {
        return prevOrders.map(o => {
          if (o.orderId.toString() === data.orderId.toString()) {
            return {
              ...o,
              fulfillment_status: 'fulfilled',
              fulfillmentStatus: 'fulfilled',
              fulfilled_at: data.timestamp,
              shopify_fulfillment_id: data.fulfillmentId,
              is_shipped: true,
              ...(data.trackingNumber && {
                finalTrackingNumber: data.trackingNumber,
                finalTrackingCompany: data.trackingCompany
              })
            };
          }
          return o;
        });
      };

      // Batch updates
      requestAnimationFrame(() => {
        setParsedOrders(updateOrderInArray);
        setAllParsedOrders(updateOrderInArray);
      });

      enqueueSnackbar(
        `Order #${data.orderId} fulfilled by ${data.updatedBy}`,
        {
          variant: 'success',
          autoHideDuration: 4000
        }
      );
    };

    // OPTIMIZED: Shipping label created handler
    const handleShippingLabelCreated = (data: {
      shop: string;
      orderId: string;
      trackingNumber: string;
      logisticsId?: string;
      paymentNo?: string;
      validationNo?: string;
      logisticsSubType?: string;
      storeId?: string;
      storeName?: string;
      updatedBy: string;
      timestamp: string;
    }) => {
      if (data.shop !== 'shopify') return;

      console.log('Received shipping label created event:', data);

      // Calculate display tracking number
      let displayTrackingNumber = '';
      if (data.trackingNumber) {
        displayTrackingNumber = data.trackingNumber;
      } else if (data.paymentNo) {
        const logisticsType = data.logisticsSubType || 'UNIMARTC2C';
        if (logisticsType === 'FAMIC2C') {
          displayTrackingNumber = data.paymentNo;
        } else {
          displayTrackingNumber = data.validationNo
            ? `${data.paymentNo}${data.validationNo}`
            : data.paymentNo;
        }
      }

      // OPTIMIZED: Single update function
      const updateOrdersWithShipping = (prevOrders: any[]) => {
        return prevOrders.map(order => {
          if (order.orderId.toString() === data.orderId.toString()) {
            const updatedItems = order.items.map((item: OrderItem) => ({
              ...item,
              trackingNumber: data.trackingNumber || data.logisticsId,
              paymentNo: data.paymentNo,
              cvsValidationNo: data.validationNo,
              ecpayLogisticsId: data.logisticsId,
              logistics_sub_type: data.logisticsSubType,
              store_id: data.storeId,
              store_name: data.storeName
            }));

            return {
              ...order,
              items: updatedItems,
              hasTrackingInfo: true,
              tracking_number: displayTrackingNumber,
              payment_no: data.paymentNo,
              cvs_validation_no: data.validationNo,
              ecpay_logistics_id: data.logisticsId,
              logistics_sub_type: data.logisticsSubType,
              store_id: data.storeId,
              store_name: data.storeName
            };
          }
          return order;
        });
      };

      // Batch updates
      requestAnimationFrame(() => {
        setParsedOrders(updateOrdersWithShipping);
        setAllParsedOrders(updateOrdersWithShipping);
      });

      enqueueSnackbar(
        `Shipping label created for Order #${data.orderId} by ${data.updatedBy}`,
        {
          variant: 'success',
          autoHideDuration: 4000
        }
      );
    };

    // Background sync found new orders - refetch from cached backend
    const handleOrdersUpdated = (data: any) => {
      console.log('Background sync detected changes:', data);
      fetchShopifyOrdersComplete(false);
    };

    // Register event listeners only once
    socket.on('tracking-update', handleTrackingUpdate);
    socket.on('order-fulfilled', handleOrderFulfilled);
    socket.on('shipping-label-created', handleShippingLabelCreated);
    socket.on('shopify-orders-updated', handleOrdersUpdated);

    // Cleanup on unmount or socket change
    return () => {
      socket.off('tracking-update', handleTrackingUpdate);
      socket.off('order-fulfilled', handleOrderFulfilled);
      socket.off('shipping-label-created', handleShippingLabelCreated);
      socket.off('shopify-orders-updated', handleOrdersUpdated);
    };
  }, [socket, getProductKey, setParsedOrders, setAllParsedOrders, setProductMetadata, enqueueSnackbar, fetchShopifyOrdersComplete]);

  // Memoized OrderCard component
  const OrderCard = React.memo(
    ({
      order,
      index,
      listRef,
    }: { order: any; index: number; listRef: any }) => {
      const cardRef = useRef<HTMLDivElement>(null);
      const [showGallery, setShowGallery] = useState(false);
      const [galleryLoaded, setGalleryLoaded] = useState(false);
      const [productLinks, setProductLinks] = useState<{ [key: string]: ProductInfo }>({});
      const [loadingLinks, setLoadingLinks] = useState(false);
      const stableOrderId = order.orderId;
      const stableItemsLength = order.items?.length || 0;
      const stableReceivedStatus = order.receivedStatus;
      const stableHasTrackingInfo = order.hasTrackingInfo;
      const stableIsFiltered = order.isFiltered;

      const toggleGallery = useCallback(() => {
        if (showGallery) {
          setShowGallery(false);
          setGalleryLoaded(false);

          const currentCardRef = cardRef.current;
          const currentListRef = listRef.current;

          if (currentCardRef && currentListRef) {
            setTimeout(() => {
              measuredHeightsRef.current[index] = currentCardRef.getBoundingClientRect().height;
              currentListRef.resetAfterIndex(index);
            }, 50);
          }
        } else {
          setShowGallery(true);
          setGalleryLoaded(false);
        }
      }, [stableOrderId, stableItemsLength, stableReceivedStatus, showGallery, index, listRef]);

      // Optimized product links fetching
      useEffect(() => {
        const fetchProductLinks = async () => {
          if (!order.items || order.items.length === 0) return;

          setLoadingLinks(true);
          try {
            const itemsWithSku = order.items.filter((item: OrderItem) => item.sku);
            if (itemsWithSku.length === 0) {
              setLoadingLinks(false);
              return;
            }

            const storedExcelData = localStorage.getItem('productLinksData');
            let productsList: ExcelProductData[] = [];

            if (storedExcelData) {
              try {
                productsList = JSON.parse(storedExcelData);
                console.log(`Using ${productsList.length} products from Excel data`);
              } catch (error) {
                console.error('Error parsing Excel data:', error);
                productsList = await productLinksService.fetchProductLinks() as ExcelProductData[];
              }
            } else {
              productsList = await productLinksService.fetchProductLinks() as ExcelProductData[];
            }

            const productInfoMap: { [key: string]: ProductInfo } = {};

            order.items.forEach((item: OrderItem) => {
              let matchingProduct: ExcelProductData | undefined;

              if (item.sku) {
                matchingProduct = productsList.find(p => p.id === item.sku);
              }

              if (matchingProduct) {
                // Check if the product has multiple links
                if (matchingProduct.allLinks && Array.isArray(matchingProduct.allLinks) && matchingProduct.allLinks.length > 0) {
                  productInfoMap[item.id] = {
                    link: decodeHtmlEntities(matchingProduct.link || matchingProduct.allLinks[0] || ''),
                    allLinks: matchingProduct.allLinks.map((link: string) => decodeHtmlEntities(link))
                  };
                } else if (matchingProduct.link) {
                  let link = matchingProduct.link;
                  link = decodeHtmlEntities(link);
                  if (typeof link === 'string') {
                    productInfoMap[item.id] = {
                      link
                    };
                  }
                }
              }
            });

            setProductLinks(productInfoMap);
          } catch (error) {
            console.error('Error fetching product links:', error);
          } finally {
            setLoadingLinks(false);
          }
        };

        fetchProductLinks();
      }, [order.items, decodeHtmlEntities]);

      useEffect(() => {
        if (showGallery && !galleryLoaded) {
          const timer = setTimeout(() => {
            setGalleryLoaded(true);
          }, 100);
          return () => clearTimeout(timer);
        }
      }, [showGallery, galleryLoaded]);

      // Optimized height measurement
      useEffect(() => {
        const measureHeight = () => {
          if (!cardRef.current) return;

          const newHeight = cardRef.current.getBoundingClientRect().height;
          const currentHeight = measuredHeightsRef.current[index] || 0;

          // Only update if height changed significantly AND we're not just updating received status
          if (newHeight > 0 && Math.abs(newHeight - currentHeight) > 20) {
            measuredHeightsRef.current[index] = newHeight;
            if (listRef.current) {
              // Don't force scroll position change
              listRef.current.resetAfterIndex(index, false);
            }
          }
        };

        // Measure after a short delay to avoid interference with state updates
        const timer = setTimeout(measureHeight, 50);

        return () => clearTimeout(timer);
      }, [
        stableOrderId,
        stableItemsLength,
        stableReceivedStatus,
        stableHasTrackingInfo,
        stableIsFiltered,
        index,
        listRef,
        showGallery,
        galleryLoaded
      ]);

      const cardStatus = order.allItemsReceived ? 'ready' as const
        : order.receivedStatus === 'partial' ? 'partial' as const
        : order.hasTrackingInfo ? 'shipped' as const
        : 'pending' as const;

      return (
        <Card ref={cardRef} sx={{
          ...S.orderCard(cardStatus),
          mb: 0,
          mx: 0,
          width: '100%',
          boxSizing: 'border-box',
        }}>
          <CardHeader
            title={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                {/* Add batch selection checkbox */}
                {(order.tracking_number || order.payment_no) && (
                  <Checkbox
                    checked={selectedOrders.includes(order.orderId)}
                    onChange={() => toggleOrderSelection(order.orderId)}
                    size="small"
                    color="primary"
                    sx={{ '&:hover': { transform: 'scale(1.05)' }, transition: 'transform 0.15s ease' }}
                  />
                )}

                <Typography variant="h6" sx={{ fontWeight: 700, ...S.mono, fontSize: '1.05rem', color: '#1a1a1a' }}>
                  Order #{order.orderNumber}
                </Typography>

                {/* Ready to Ship Status */}
                {order.readyToShip && (
                  <Chip
                    icon={<LocalShippingIcon />}
                    label="可出貨 ✓"
                    color="success"
                    size="small"
                    sx={S.statusChip}
                  />
                )}

                {/* Received Status */}
                {order.receivedStatus === 'complete' && !order.readyToShip && (
                  <Chip
                    icon={<CheckCircleIcon />}
                    label="已收齊"
                    color="success"
                    size="small"
                    sx={S.statusChip}
                  />
                )}
                {order.receivedStatus === 'partial' && (
                  <Chip
                    icon={<WarningIcon />}
                    label={`已收 ${order.receivedItemsInOrder}/${order.totalItemsInOrder}`}
                    color="warning"
                    size="small"
                    sx={S.statusChip}
                  />
                )}
                {order.receivedStatus === 'pending' && (
                  <Chip
                    icon={<ErrorIcon />}
                    label="未收到"
                    color="error"
                    size="small"
                    sx={S.statusChip}
                  />
                )}

                {/* Progress Bar */}
                {order.totalItemsInOrder > 0 && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 140 }}>
                    <LinearProgress
                      variant="determinate"
                      value={(order.receivedItemsInOrder / order.totalItemsInOrder) * 100}
                      color={order.allItemsReceived ? 'success' : order.receivedStatus === 'partial' ? 'warning' : 'error'}
                      sx={{ flex: 1, height: 8, borderRadius: 4 }}
                    />
                    <Typography variant="caption" fontWeight="bold" sx={{ whiteSpace: 'nowrap' }}>
                      {order.receivedItemsInOrder}/{order.totalItemsInOrder}
                    </Typography>
                  </Box>
                )}

                {/* Filtered Items Indicator */}
                {order.isFiltered && (
                  <Chip
                    label={`顯示 ${order.filteredItemCount}/${order.originalItemCount} 件`}
                    variant="outlined"
                    size="small"
                    color="primary"
                  />
                )}
              </Box>
            }
            subheader={
              <Grid container spacing={2} sx={{ color: '#5c5854', fontSize: '0.875rem' }}>
                <Grid item xs={12} md={4}>
                  訂單日期: {order.createdAt}
                </Grid>

                <Grid item xs={12} md={4}>
                  Status: {order.status}
                  {order.status === 'pending' && ' (還沒付款)'}
                  {order.status === 'paid' && ' (已付款)'}
                  {order.readyToShip && (
                    <Typography component="span" sx={{ ml: 1, color: 'success.main', fontWeight: 'bold' }}>
                      → READY TO SHIP
                    </Typography>
                  )}
                </Grid>

                {order.ecpayLogisticsId && !order.trackingNumber && (
                  <Grid item xs={12} md={4}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => fetchTrackingNumber(order)}
                      startIcon={<RefreshIcon />}
                    >
                      獲取追蹤號碼
                    </Button>
                  </Grid>
                )}

                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary">
                    追蹤號碼 (Tracking Number)
                  </Typography>

                  {order.tracking_number || order.payment_no ? (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="body1" sx={{ mr: 1, ...S.mono }}>
                        {order.tracking_number || (
                          order.logistics_sub_type === 'FAMIC2C' ||
                            (order.store_id && (order.store_id.startsWith('F') || /^[A-Z]\d{5}$/.test(order.store_id)))
                            ? order.payment_no
                            : order.cvs_validation_no
                              ? `${order.payment_no}${order.cvs_validation_no}`
                              : order.payment_no
                        )}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={() => copyToClipboard(
                          order.tracking_number || (
                            order.logistics_sub_type === 'FAMIC2C' ||
                              (order.store_id && (order.store_id.startsWith('F') || /^[A-Z]\d{5}$/.test(order.store_id)))
                              ? order.payment_no
                              : order.cvs_validation_no
                                ? `${order.payment_no}${order.cvs_validation_no}`
                                : order.payment_no
                          )
                        )}
                        title="複製追蹤碼"
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ) : (
                    <Typography variant="body1" color="text.secondary">
                      尚未出貨
                    </Typography>
                  )}
                </Grid>

                {/* Enhanced summary info */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="body2">
                      <strong>商品數量:</strong> {order.totalItemsInOrder}
                    </Typography>
                    <Typography variant="body2">
                      <strong>已收到:</strong> {order.receivedItemsInOrder}/{order.totalItemsInOrder}
                    </Typography>
                    {order.isFiltered && (
                      <Typography variant="body2" color="primary">
                        <strong>顯示:</strong> {order.filteredItemCount} matching items
                      </Typography>
                    )}
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  Customer: {order.customer}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 60 }}>
                      地址:
                    </Typography>
                    <LogisticsTypeChip order={order} />

                    {/* Address editing for home delivery orders */}
                    {(() => {
                      const { label } = getLogisticsTypeLabel(order);
                      const isHomeDelivery = label === '宅配';
                      const currentAddress = addressEdits[order.orderId] ?? order.shippingAddress;
                      const validation = validateTaiwanAddress(currentAddress);
                      const isEditing = editingAddress[order.orderId];

                      if (isHomeDelivery) {
                        return (
                          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {isEditing ? (
                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', width: '100%' }}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  multiline
                                  rows={2}
                                  value={currentAddress}
                                  onChange={(e) => handleAddressEdit(order.orderId, e.target.value)}
                                  error={!validation.isValid}
                                  helperText={
                                    !validation.isValid
                                      ? `建議補充: ${validation.missing.join(', ')} (仍可儲存)`
                                      : '地址格式正確'
                                  }
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      '&.Mui-error': {
                                        backgroundColor: 'rgba(255, 152, 0, 0.05)' // Warning color instead of error
                                      }
                                    }
                                  }}
                                />
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                  <Tooltip title={!validation.isValid ? `建議補充 ${validation.missing.join(', ')}，但仍可儲存` : '儲存地址'}>
                                    <IconButton
                                      size="small"
                                      color={!validation.isValid ? "warning" : "primary"}
                                      onClick={() => handleSaveAddress(order)}
                                    // ✅ REMOVED: disabled={!validation.isValid}
                                    >
                                      <CheckCircleIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <IconButton
                                    size="small"
                                    color="secondary"
                                    onClick={() => handleCancelAddressEdit(order.orderId)}
                                  >
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                              </Box>
                            ) : (
                              <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                flex: 1,
                                backgroundColor: !validation.isValid ? 'rgba(255, 152, 0, 0.08)' : 'transparent',
                                padding: !validation.isValid ? '8px' : '0',
                                borderRadius: 1,
                                border: !validation.isValid ? '1px solid #ff9800' : 'none'
                              }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    flex: 1,
                                    color: !validation.isValid ? 'warning.main' : 'text.primary' // Changed from error to warning
                                  }}
                                >
                                  {currentAddress}
                                </Typography>

                                {!validation.isValid && (
                                  <Chip
                                    icon={<WarningIcon />}
                                    label={`建議補充: ${validation.missing.join(', ')}`}
                                    color="warning"
                                    size="small"
                                    variant="outlined"
                                  />
                                )}

                                <Tooltip title="編輯地址">
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      setEditingAddress(prev => ({ ...prev, [order.orderId]: true }));
                                      setAddressEdits(prev => ({
                                        ...prev,
                                        [order.orderId]: order.shippingAddress
                                      }));
                                    }}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            )}
                          </Box>
                        );
                      } else {
                        // Regular display for convenience store orders
                        return (
                          <>
                            <Typography variant="body2" sx={{ flex: 1 }}>
                              {order.shippingAddress}
                            </Typography>
                          </>
                        );
                      }
                    })()}
                  </Box>

                  {/* Show store info if available */}
                  {order.store_id && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, ml: 7 }}>
                      <Typography variant="caption" color="text.secondary">
                        門市代碼:
                      </Typography>
                      <Typography variant="caption" sx={S.mono}>
                        {order.store_id}
                      </Typography>
                      {order.store_name && (
                        <>
                          <Typography variant="caption" color="text.secondary">
                            |
                          </Typography>
                          <Typography variant="caption">
                            {order.store_name}
                          </Typography>
                        </>
                      )}
                    </Box>
                  )}
                </Grid>
              </Grid>
            }
            action={
              <Box sx={{
                display: 'flex',
                gap: 1,
                flexWrap: 'wrap',
                justifyContent: 'flex-end',
                alignItems: 'center',
                '@media (max-width: 900px)': {
                  width: '100%',
                  justifyContent: 'flex-start',
                  mt: 2
                }
              }}>

                <Button
                  variant="outlined"
                  color="primary"
                  onClick={toggleGallery}
                  size="small"
                  startIcon={showGallery ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600, transition: 'all 0.15s ease', borderColor: '#e8e5e1', color: '#5c5854', '&:hover': { borderColor: '#d1cdc8', bgcolor: 'rgba(0,0,0,0.02)' } }}
                >
                  {showGallery ? '隱藏圖庫' : '顯示圖庫'}
                </Button>



                {/* Open All Order Links Button */}
                {(() => {
                  const itemsWithLinks = order.items.filter((item: OrderItem) =>
                    productLinks[item.id]?.link || (productLinks[item.id]?.allLinks && productLinks[item.id].allLinks!.length > 0)
                  );

                  if (itemsWithLinks.length === 0) return null;

                  return (
                    <Button
                      variant="outlined"
                      color="secondary"
                      size="small"
                      startIcon={<OpenInNewIcon />}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        const allLinksToOpen: string[] = [];
                        order.items.forEach((item: OrderItem) => {
                          const info = productLinks[item.id];
                          if (info?.allLinks && info.allLinks.length > 0) {
                            info.allLinks.forEach(l => { if (!allLinksToOpen.includes(l)) allLinksToOpen.push(l); });
                          } else if (info?.link) {
                            if (!allLinksToOpen.includes(info.link)) allLinksToOpen.push(info.link);
                          }
                        });

                        allLinksToOpen.forEach((link, index) => {
                          setTimeout(() => {
                            window.open(link, '_blank');
                          }, index * 100);
                        });

                        enqueueSnackbar(`開啟 ${allLinksToOpen.length} 個連結...`, { variant: 'info' });
                      }}
                    >
                      Open All Links ({itemsWithLinks.length})
                    </Button>
                  );
                })()}



                {/* Individual print button for orders with tracking info */}
                {order.hasTrackingInfo && order.deliveryMethod === 'pickup' && (
                  <IconButton
                    size="small"
                    color="secondary"
                    title="列印出貨單"
                    onClick={() => handlePrintShippingDocument(order)}
                  >
                    <PrintIcon />
                  </IconButton>
                )}

                {/* Enhanced shipping buttons based on ready status */}


                {/* Enhanced shipping buttons based on ready status */}
                {order.hasTrackingInfo && (
                  <Chip
                    icon={<LocalShippingIcon />}
                    label="已建立物流單"
                    color="info"
                    size="small"
                    sx={{ ml: 1 }}
                  />
                )}

                {/* Updated fulfillment button with tracking number requirement */}
                <Tooltip
                  title={
                    !order.allItemsReceived
                      ? "All items must be received before fulfillment"
                      : isTrackingRequired(order) && !(order.tracking_number || order.payment_no)
                        ? "Tracking number is required for fulfillment"
                        : "Mark order as fulfilled in Shopify"
                  }
                  arrow
                >
                  <span>
                    <Button
                      variant="outlined"
                      color="primary"
                      startIcon={<ShippingIcon />}
                      onClick={() => handleFulfillOrder(order)}
                      size="small"
                      disabled={
                        !order.allItemsReceived
                      }
                    >
                      已出貨
                    </Button>
                  </span>
                </Tooltip>



                {/* Quick Ship button - only show for convenience store orders without tracking */}
                {!order.hasTrackingInfo && order.store_id && order.deliveryMethod === 'pickup' && (
                  <Tooltip
                    title={
                      !order.allItemsReceived
                        ? "All items must be received before quick shipping"
                        : "使用預設資訊快速建立物流單"
                    }
                    arrow
                  >
                    <span>
                      <Button
                        variant="contained"
                        color="success"
                        size="small"
                        startIcon={<LocalShippingIcon />}
                        onClick={() => handleQuickShip(order)}
                        disabled={!order.allItemsReceived}
                      >
                        快速出貨
                      </Button>
                    </span>
                  </Tooltip>
                )}

                {/* Smart shipping button based on logistics type */}
                {(() => {
                  const { label } = getLogisticsTypeLabel(order);
                  const isHomeDelivery = label === '宅配';

                  return (
                    <Button
                      variant="contained"
                      color={isHomeDelivery ? "warning" : "primary"}
                      startIcon={<ShippingIcon />}
                      onClick={() => {
                        if (isHomeDelivery) {
                          // Direct Hsinchu integration for home delivery
                          handleHsinchuShipping(order);
                        } else {
                          setShippingDialogData(order);
                        }
                      }}
                      size="small"
                    >
                      {isHomeDelivery ? '宅配出貨' : '超商出貨'}
                    </Button>
                  );
                })()}
              </Box>
            }
          />

          <Divider />

          {/* PROMINENT READY TO SHIP BANNER */}
          {order.allItemsReceived && !order.hasTrackingInfo && (
            <Alert
              severity="success"
              icon={<LocalShippingIcon />}
              sx={{
                m: 2,
                border: '1.5px solid #22c55e',
                borderRadius: '12px',
                animation: 'pulseGlow 2s ease-in-out 3',
                '@keyframes pulseGlow': {
                  '0%, 100%': { boxShadow: 'none' },
                  '50%': { boxShadow: '0 0 16px rgba(34,197,94,0.3)' }
                }
              }}
            >
              <Typography variant="body1" fontWeight="bold">
                ✅ 全部商品已收到！可以出貨
              </Typography>
            </Alert>
          )}

          {/* Enhanced warning if items are filtered */}
          {order.isFiltered && (() => {
            const totalItems = order.originalItemCount || order.totalItemsInOrder || 0;
            const receivedItems = order.receivedItemsInOrder || 0;
            const remainingUnreceived = totalItems - receivedItems;
            return (
              <Alert
                severity="warning"
                icon={<WarningIcon />}
                sx={{ m: 2 }}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => {
                      setExpandedOrders(prev => {
                        const newSet = new Set(prev);
                        if (newSet.has(order.orderId)) {
                          newSet.delete(order.orderId);
                        } else {
                          newSet.add(order.orderId);
                        }
                        return newSet;
                      });
                    }}
                  >
                    {order.isExpanded ? '僅顯示搜尋結果' : '顯示全部商品'}
                  </Button>
                }
              >
                <Box>
                  <Typography variant="body2" fontWeight="bold">
                    ⚠️ 搜尋結果僅顯示 {order.filteredItemCount}/{totalItems} 件商品
                  </Typography>
                  <Typography variant="body2">
                    此訂單共 {totalItems} 件商品，已收到 {receivedItems}/{totalItems} 件
                    {remainingUnreceived > 0 && ` — 還有 ${remainingUnreceived} 件未收到`}
                    {remainingUnreceived > 0 && order.filteredItemCount < totalItems && '（部分未顯示在搜尋結果中）'}
                  </Typography>
                  {order.allItemsReceived && (
                    <Typography variant="body2" fontWeight="bold" color="success.main" sx={{ mt: 0.5 }}>
                      ✅ 全部商品已收到，可以出貨!
                    </Typography>
                  )}
                </Box>
              </Alert>
            );
          })()}

          {showGallery && (
            <Box
              sx={{ p: 2, bgcolor: '#fafaf8' }}
              onLoad={() => {
                const currentCardRef = cardRef.current;
                const currentListRef = listRef.current;

                if (currentCardRef && currentListRef) {
                  setTimeout(() => {
                    measuredHeightsRef.current[index] = currentCardRef.getBoundingClientRect().height;
                    currentListRef.resetAfterIndex(index);
                  }, 50);
                }
              }}
            >
              {/* Enhanced Product Image Gallery */}
              <Grid container spacing={2}>
                {order.items.map((item: OrderItem) => (
                  <Grid item xs={12} md={6} key={item.id}>
                    <Card variant="outlined">
                      <CardHeader
                        title={item.productName}
                        subheader={item.itemSpec}
                        titleTypographyProps={{ variant: 'h6' }}
                      />
                      <CardContent>
                        {/* Display all product images if available */}
                        {item.product_images && item.product_images.length > 0 ? (
                          <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                              Product Images ({item.product_images.length})
                            </Typography>
                            <Grid container spacing={1}>
                              {item.product_images.map((image: any) => (
                                <Grid item xs={6} sm={4} md={3} key={image.id}>
                                  <Box
                                    sx={{
                                      width: '100%',
                                      aspectRatio: '1/1',
                                      overflow: 'hidden',
                                      border: '1px solid #e8e5e1',
                                      borderRadius: 1,
                                      cursor: 'pointer',
                                      '&:hover': {
                                        boxShadow: 2
                                      }
                                    }}
                                    onClick={() => {
                                      // Optional: Open image in modal or new tab
                                      window.open(image.src, '_blank');
                                    }}
                                  >
                                    <img
                                      src={image.src}
                                      alt={image.alt || item.productName}
                                      style={{
                                        width: '100%',
                                        height: '100%',
                                        objectFit: 'cover'
                                      }}
                                      loading="lazy"
                                    />
                                  </Box>
                                </Grid>
                              ))}
                            </Grid>
                          </Box>
                        ) : (
                          // Fallback to single image if no product_images
                          <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                              Product Image
                            </Typography>
                            {item.imageUrl ? (
                              <Box
                                sx={{
                                  width: '200px',
                                  height: '200px',
                                  border: '1px solid #e8e5e1',
                                  borderRadius: 1,
                                  overflow: 'hidden',
                                  cursor: 'pointer',
                                  '&:hover': {
                                    boxShadow: 2
                                  }
                                }}
                                onClick={() => {
                                  // Convert to larger image
                                  const baseUrl = item.imageUrl.split('?')[0];
                                  const largeImage = `${baseUrl}?width=800&height=800&crop=center`;
                                  window.open(largeImage, '_blank');
                                }}
                              >
                                <img
                                  src={item.imageUrl}
                                  alt={item.productName}
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover'
                                  }}
                                  loading="lazy"
                                />
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                No image available
                              </Typography>
                            )}
                          </Box>
                        )}

                        {/* Reference Image */}
                        {item.referenceImage && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                              Reference Image
                            </Typography>
                            <Box
                              sx={{
                                width: '150px',
                                height: '150px',
                                border: '2px solid #4caf50',
                                borderRadius: 1,
                                overflow: 'hidden',
                                cursor: 'pointer',
                                '&:hover': {
                                  boxShadow: 2
                                }
                              }}
                              onClick={() => window.open(item.referenceImage, '_blank')}
                            >
                              <img
                                src={item.referenceImage}
                                alt="Reference"
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover'
                                }}
                                loading="lazy"
                              />
                            </Box>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          <CardContent sx={{
            padding: '16px',
            overflow: 'visible', // ADDED: Prevent content clipping
            '&:last-child': {
              paddingBottom: '16px'
            }
          }}>
            <TableContainer sx={{
              width: '100%',
              overflowX: 'auto',
              overflowY: 'visible',
              maxHeight: 'none',
              WebkitOverflowScrolling: 'touch', // Smooth scrolling on iOS
              '@media (max-width: 900px)': {
                overflowX: 'scroll' // Force horizontal scroll on small screens
              }
            }}>
              <Table
                size="small"
                sx={{
                  minWidth: { xs: 1200, md: 1200 }, // Responsive min-width
                  tableLayout: 'fixed',
                  '@media (max-width: 600px)': {
                    minWidth: 1000 // Slightly smaller on mobile
                  }
                }}
              >

                <TableHead sx={S.tableHeader}>
                  <TableRow>
                    <TableCell width="60px" sx={{ minWidth: '60px' }}>圖片</TableCell>
                    <TableCell width="200px" sx={{ minWidth: '200px' }}>商品</TableCell>
                    <TableCell width="150px" sx={{ minWidth: '150px' }}>規格</TableCell>
                    <TableCell width="80px" sx={{ minWidth: '80px' }}>數量</TableCell>
                    <TableCell width="120px" sx={{ minWidth: '120px' }}>標籤</TableCell>
                    <TableCell width="200px" sx={{ minWidth: '200px' }}>備註</TableCell>
                    <TableCell width="100px" sx={{ minWidth: '100px' }}>已收到</TableCell>
                    <TableCell width="150px" sx={{ minWidth: '150px' }}>參考圖</TableCell>
                    <TableCell width="120px" sx={{ minWidth: '120px' }}>品號</TableCell>
                    <TableCell width="100px" sx={{ minWidth: '100px' }}>訂購連結</TableCell>
                  </TableRow>
                </TableHead>


                <TableBody>
                  {order.items.map((item: OrderItem) => {
                    const productInfo = productLinks[item.id];
                    return (
                      <TableRow
                        key={item.id}
                        sx={S.tableRow(!!item.received)}
                      >
                        {/* Image */}
                        <TableCell sx={{ p: 1 }}>
                          {item.imageUrl && (
                            <Box sx={{ width: 60, height: 60, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <HoverZoomImage
                                src={item.imageUrl}
                                alt={item.productName}
                                width={60}
                                height={60}
                                borderColor="#2196f3"
                              />
                            </Box>
                          )}
                        </TableCell>

                        {/* Product */}
                        <TableCell>
                          <Typography variant="body2">
                            {item.productName}
                            {item.received && (
                              <CheckCircleIcon
                                sx={{ ml: 1, color: 'success.main', fontSize: 16 }}
                              />
                            )}
                          </Typography>
                        </TableCell>

                        {/* Item Spec (editable) */}
                        <TableCell>
                          <ExcelLikeField
                            value={item.itemSpec || ''}
                            onSave={(value) => handleVariantUpdate(item, value)}
                            placeholder="—"
                          />
                        </TableCell>

                        {/* Quantity */}
                        <TableCell
                          sx={item.quantity > 1 ? { bgcolor: '#fef3c7', fontWeight: 'bold', ...S.mono } : {}}
                        >
                          {item.quantity}
                        </TableCell>

                        {/* Tag */}
                        <TableCell>
                          <ExcelLikeField
                            value={item.tag || ''}
                            onSave={(value) => handleTrackingUpdate(item, 'tag', value)}
                            placeholder="Add tag..."
                          />
                        </TableCell>

                        {/* Note */}
                        <TableCell>
                          <ExcelLikeField
                            value={item.note || ''}
                            onSave={(value) => handleTrackingUpdate(item, 'note', value)}
                            placeholder="Add note..."
                            multiline
                            rows={2}
                          />
                        </TableCell>

                        {/* Received */}
                        <TableCell>
                          <Tooltip
                            title={
                              item.received && item.receivedBy ?
                                `Received by: ${item.receivedBy}${item.receivedAt ? `\nAt: ${new Date(item.receivedAt).toLocaleString()}` : ''}` :
                                "Mark as received"
                            }
                            arrow
                            placement="top"
                          >
                            <Checkbox
                              checked={item.received}
                              onChange={(e) => handleTrackingUpdate(item, 'received', e.target.checked)}
                              sx={{ '&:hover': { transform: 'scale(1.05)' }, transition: 'transform 0.15s ease' }}
                            />
                          </Tooltip>
                        </TableCell>


                        {/* Reference Image */}
                        <TableCell sx={{ p: 1 }}>
                          {item.referenceImage ? (
                            <Box sx={{
                              position: 'relative',
                              width: 40,
                              height: 40,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              {/* Use HoverZoomImage component */}
                              <HoverZoomImage
                                src={item.referenceImage}
                                alt="Reference"
                                width={40}
                                height={40}
                                borderColor="#4caf50"
                              />

                              {/* Remove button with proper z-index management */}
                              <IconButton
                                size="small"
                                sx={{
                                  position: 'absolute',
                                  top: -8,
                                  right: -8,
                                  backgroundColor: 'white',
                                  zIndex: 100010, // Higher than the zoom overlay (99999)
                                  '&:hover': {
                                    backgroundColor: 'rgba(255,255,255,0.9)',
                                    zIndex: 100011 // Even higher on hover
                                  },
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)', // Add shadow for visibility
                                  border: '1px solid #ddd' // Add border for definition
                                }}
                                onClick={(e) => {
                                  e.stopPropagation(); // Prevent any parent click events
                                  e.preventDefault(); // Prevent default behavior
                                  handleRemoveReferenceImage(item);
                                }}
                                onMouseDown={(e) => {
                                  e.stopPropagation(); // Prevent interference with hover
                                }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          ) : (
                            <Button
                              component="label"
                              variant="outlined"
                              size="small"
                              sx={{ fontSize: '0.7rem', p: 0.5 }}
                            >
                              Upload
                              <input
                                type="file"
                                hidden
                                accept="image/*"
                                onChange={(e) => handleReferenceImageUpload(e, item)}
                              />
                            </Button>
                          )}
                        </TableCell>

                        {/* Item Number (SKU) */}
                        <TableCell>
                          <ExcelLikeField
                            value={item.sku || ''}
                            onSave={(value) => handleSkuUpdate(item, value)}
                            placeholder="Add SKU..."
                          />
                        </TableCell>

                        {/* Order Link */}
                        <TableCell>
                          {loadingLinks ? (
                            <CircularProgress size={20} />
                          ) : productInfo?.allLinks && productInfo.allLinks.length > 0 ? (
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              {productInfo.allLinks.map((link, idx) => (
                                <Tooltip key={idx} title={`開啟連結 ${idx + 1}`}>
                                  <IconButton
                                    onClick={(e) => {
                                      e.preventDefault();
                                      window.open(link, '_blank', 'noopener,noreferrer');
                                    }}
                                    sx={{
                                      color: idx === 0 ? '#3b82f6' : '#8b7355',
                                      width: 32,
                                      height: 32,
                                      border: '1px solid',
                                      borderColor: idx === 0 ? '#bfdbfe' : '#d1cdc8',
                                      borderRadius: 1,
                                      fontSize: '0.7rem',
                                      '&:hover': { bgcolor: idx === 0 ? '#eff6ff' : '#f5f3f0' },
                                    }}
                                  >
                                    <OpenInNewIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                              ))}
                            </Box>
                          ) : productInfo && productInfo.link ? (
                            <Tooltip title="Open product ordering page">
                              <IconButton
                                onClick={(e) => {
                                  e.preventDefault();
                                  window.open(productInfo.link, '_blank', 'noopener,noreferrer');
                                }}
                                sx={{
                                  color: '#3b82f6',
                                  width: 32,
                                  height: 32,
                                  border: '1px solid #bfdbfe',
                                  borderRadius: 1,
                                  '&:hover': { bgcolor: '#eff6ff' },
                                }}
                              >
                                <OpenInNewIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                              -
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Show summary if items are filtered */}
            {order.isFiltered && (
              <Box sx={{ mt: 2, p: 2, backgroundColor: 'rgba(0, 0, 0, 0.05)', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  📊 <strong>Order Summary:</strong> {order.receivedItemsInOrder}/{order.totalItemsInOrder} items received
                  {order.allItemsReceived && (
                    <Typography component="span" sx={{ ml: 2, color: 'success.main', fontWeight: 'bold' }}>
                      ✅ All items received - Ready to ship!
                    </Typography>
                  )}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      );
    });

  const handleToggleOrderHidden = useCallback(async (order: { is_hidden: any; orderNumber: any; orderId: any; }) => {
    try {
      const newHiddenState = !order.is_hidden;

      const response = await api.put(`/api/shopify/orders/${order.orderNumber}/hidden`, {
        hidden: newHiddenState
      });

      if (response.data.success) {
        // If showing hidden orders, update the order in place
        if (showHiddenOrders) {
          const updateOrderInArray = (prevOrders: any[]) => {
            return prevOrders.map(o => {
              if (o.orderId === order.orderId) {
                return { ...o, is_hidden: newHiddenState };
              }
              return o;
            });
          };

          setParsedOrders(updateOrderInArray);
          setAllParsedOrders(updateOrderInArray);
        } else {
          // If not showing hidden orders, remove the order from the list
          const removeOrderFromArray = (prevOrders: any[]) => {
            return prevOrders.filter(o => o.orderId !== order.orderId);
          };

          setParsedOrders(removeOrderFromArray);
          setAllParsedOrders(removeOrderFromArray);
        }

        enqueueSnackbar(
          newHiddenState ? 'Order hidden' : 'Order unhidden',
          { variant: 'success' }
        );
      }
    } catch (error) {
      console.error('Error toggling order hidden status:', error);
      enqueueSnackbar('Failed to update order visibility', { variant: 'error' });
    }
  }, [enqueueSnackbar, showHiddenOrders, setParsedOrders, setAllParsedOrders]);

  // Add print shipping document handler
  const handlePrintShippingDocument = useCallback(async (order: any) => {
    if (!order || !order.orderId) return;

    try {
      enqueueSnackbar('正在準備出貨單...', { variant: 'info' });

      if (order.deliveryMethod === 'pickup' && order.store_id) {
        const response = await api.post(`/api/ecpay/logistics/print/${order.orderNumber}`, {
          platform: 'shopify',
          logistics_sub_type: order.logistics_sub_type
        });

        if (response && response.data) {
          const blob = new Blob([response.data], { type: 'text/html' });
          const url = URL.createObjectURL(blob);
          window.open(url, '_blank');
          setTimeout(() => URL.revokeObjectURL(url), 100);

          enqueueSnackbar('出貨單已在新視窗中開啟', { variant: 'success' });
        } else {
          throw new Error('Invalid response format');
        }
      } else {
        enqueueSnackbar('宅配訂單需要手動處理', { variant: 'warning' });
      }
    } catch (error) {
      console.error('Error printing shipping document:', error);
      enqueueSnackbar('無法產生出貨單: ' + ((error as Error).message || '未知錯誤'), {
        variant: 'error',
      });
    }
  }, [enqueueSnackbar]);

  const handleSyncFulfillment = useCallback(async () => {
    try {
      // Count unfulfilled orders first
      const unfulfilledOrders = parsedOrders.filter(order =>
        !order.fulfillment_status ||
        order.fulfillment_status === 'unfulfilled' ||
        order.fulfillment_status === null
      );

      const unfulfilledCount = unfulfilledOrders.length;

      // If no unfulfilled orders, show success and exit
      if (unfulfilledCount === 0) {
        enqueueSnackbar('All orders are already fulfilled! 🎉', {
          variant: 'success',
          autoHideDuration: 3000
        });
        return;
      }

      setLoading(true);
      enqueueSnackbar(
        `Checking ${unfulfilledCount} unfulfilled order${unfulfilledCount !== 1 ? 's' : ''} from Shopify...`,
        { variant: 'info' }
      );

      const response = await api.post('/api/shopify/orders/sync-fulfillment');

      if (response.data.success) {
        const { checkedCount, updatedCount } = response.data;

        // Reload the orders after fulfillment sync
        await fetchShopifyOrdersComplete();

        // Show different messages based on results
        if (updatedCount > 0) {
          enqueueSnackbar(
            `✅ ${updatedCount} order${updatedCount !== 1 ? 's' : ''} updated! (Checked ${checkedCount})`,
            {
              variant: 'success',
              autoHideDuration: 5000
            }
          );
        } else {
          enqueueSnackbar(
            `No changes found. All ${checkedCount} unfulfilled orders are still unfulfilled.`,
            {
              variant: 'info',
              autoHideDuration: 4000
            }
          );
        }
      }
    } catch (error) {
      console.error('Fulfillment sync failed:', error);
      enqueueSnackbar('Failed to sync fulfillment status', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [parsedOrders, enqueueSnackbar, fetchShopifyOrdersComplete]);

  // Add after your existing useCallback functions, before the OrderCard component

  function hexToBase64(hexString: string) {
    // Convert hex string to bytes
    const bytes = [];
    for (let i = 0; i < hexString.length; i += 2) {
      bytes.push(parseInt(hexString.substr(i, 2), 16));
    }

    // Convert bytes to base64
    const uint8Array = new Uint8Array(bytes);
    let binaryString = '';
    for (let i = 0; i < uint8Array.length; i++) {
      binaryString += String.fromCharCode(uint8Array[i]);
    }

    return btoa(binaryString);
  }

  // Function to display shipping label in new tab
  const displayShippingLabel = useCallback((hexImage: string, labelData: HsinchuLabelData = {}) => {
    if (!hexImage) {
      console.error('No image data provided');
      return;
    }

    // Convert hex to base64
    const base64Image = hexToBase64(hexImage);

    // Create a new window/tab
    const newWindow = window.open('', '_blank');

    if (!newWindow) {
      enqueueSnackbar('Please allow popups to view the shipping label', { variant: 'warning' });
      return;
    }

    // Create HTML content for the label
    const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>宅配出貨單 - ${labelData.trackingNumber || '追蹤號碼'}</title>
      <style>
        body {
          margin: 0;
          padding: 20px;
          font-family: Arial, sans-serif;
          background-color: #f5f5f5;
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        
        .header {
          text-align: center;
          margin-bottom: 20px;
          background: white;
          padding: 15px;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          width: 100%;
          max-width: 800px;
        }
        
        .header h1 {
          color: #2c3e50;
          margin: 0 0 10px 0;
        }
        
        .tracking-info {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 10px;
          margin-top: 15px;
          text-align: left;
        }
        
        .info-item {
          background: #f8f9fa;
          padding: 8px 12px;
          border-radius: 4px;
          border-left: 3px solid #3498db;
        }
        
        .info-label {
          font-weight: bold;
          color: #2c3e50;
          font-size: 0.9em;
        }
        
        .info-value {
          color: #34495e;
          margin-top: 2px;
        }
        
        .label-container {
          background: white;
          padding: 20px;
          border-radius: 8px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          max-width: 100%;
          overflow: auto;
        }
        
        .shipping-label {
          max-width: 100%;
          height: auto;
          border: 1px solid #ddd;
          border-radius: 4px;
        }
        
        .actions {
          margin-top: 20px;
          text-align: center;
        }
        
        .btn {
          background: #3498db;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 14px;
          margin: 0 5px;
          transition: background-color 0.3s;
        }
        
        .btn:hover {
          background: #2980b9;
        }
        
        .btn-print {
          background: #27ae60;
        }
        
        .btn-print:hover {
          background: #229954;
        }
        
        .btn-download {
          background: #e74c3c;
        }
        
        .btn-download:hover {
          background: #c0392b;
        }
        
        @media print {
          body {
            background: white;
            padding: 0;
          }
          
          .header, .actions {
            display: none;
          }
          
          .label-container {
            box-shadow: none;
            padding: 0;
            margin: 0;
          }
        }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>🚚 宅配出貨單</h1>
        <p>新竹物流 Hsinchu Logistics</p>
        
        ${labelData.trackingNumber ? `
        <div class="tracking-info">
          <div class="info-item">
            <div class="info-label">追蹤號碼</div>
            <div class="info-value">${labelData.trackingNumber}</div>
          </div>
          ${labelData.recipientName ? `
          <div class="info-item">
            <div class="info-label">收件人</div>
            <div class="info-value">${labelData.recipientName}</div>
          </div>
          ` : ''}
          ${labelData.recipientAddress ? `
          <div class="info-item">
            <div class="info-label">收件地址</div>
            <div class="info-value">${labelData.recipientAddress}</div>
          </div>
          ` : ''}
          ${labelData.weight ? `
          <div class="info-item">
            <div class="info-label">重量</div>
            <div class="info-value">${labelData.weight} kg</div>
          </div>
          ` : ''}
        </div>
        ` : ''}
      </div>
      
      <div class="label-container">
        <img src="data:image/png;base64,${base64Image}" 
             alt="Shipping Label" 
             class="shipping-label"
             onload="console.log('Label image loaded successfully')"
             onerror="console.error('Failed to load label image')">
      </div>
      
      <div class="actions">
        <button class="btn btn-print" onclick="printLabel()">🖨️ 列印</button>
        <button class="btn btn-download" onclick="downloadLabel()">💾 下載</button>
        <button class="btn" onclick="window.close()">❌ 關閉</button>
      </div>
      
      <script>
        function printLabel() {
          window.print();
        }
        
        function downloadLabel() {
          const link = document.createElement('a');
          link.href = 'data:image/png;base64,${base64Image}';
          link.download = '宅配出貨單_${labelData.trackingNumber || new Date().getTime()}.png';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
        
        // Auto-focus for better UX
        window.focus();
      </script>
    </body>
    </html>
  `;

    // Write content to new window
    newWindow.document.write(htmlContent);
    newWindow.document.close();
  }, [hexToBase64, enqueueSnackbar]);

  // Function to handle Hsinchu shipping order creation
  // Function to handle Hsinchu shipping order creation
  const handleHsinchuShipping = useCallback(async (order: any) => {
    try {
      enqueueSnackbar('正在建立宅配出貨單...', { variant: 'info' });

      const response = await api.post('/api/hsinchu/create-order', {
        orderId: order.orderId, // Fixed: was 'order.orderId' (string) instead of order.orderId
        recipientName: order.customer,
        recipientPhone: order.customerPhone,
        recipientAddress: order.shippingAddress,
        weight: 1,
        itemCount: order.items.length,
        notes: '',
        declaredValue: 0,
        productType: '001'
      });

      if (response.data.success && response.data.data) {
        const result = response.data.data;

        // Display the shipping label - pass the hex image directly
        displayShippingLabel(result.labelImage, {
          trackingNumber: result.trackingNumber,
          recipientName: order.customer,
          recipientAddress: order.shippingAddress,
          weight: result.weight
        });

        // Update order state with tracking info - PRESERVE EXISTING ITEM DATA
        const updateOrdersWithTracking = (prevOrders: any[]) => {
          return prevOrders.map(o => {
            if (o.orderId === order.orderId) {
              // Update items with tracking info while preserving all existing item data
              const updatedItems = o.items.map((item: OrderItem) => ({
                ...item, // Preserve ALL existing item data including received status
                trackingNumber: result.trackingNumber,
                hsinchuTrackingNumber: result.trackingNumber,
                // Don't override other tracking fields unless necessary
                delivery_method: 'home_delivery'
              }));

              return {
                ...o, // Preserve ALL existing order data
                items: updatedItems,
                hasTrackingInfo: true,
                tracking_number: result.trackingNumber,
                hsinchuTrackingNumber: result.trackingNumber,
                delivery_method: 'home_delivery'
              };
            }
            return o;
          });
        };

        setParsedOrders(updateOrdersWithTracking);
        setAllParsedOrders(updateOrdersWithTracking);

        enqueueSnackbar(`宅配出貨單已建立！追蹤號碼: ${result.trackingNumber}`, {
          variant: 'success',
          autoHideDuration: 6000
        });

        try {
          const userId = currentUser?.id?.toString() || localStorage.getItem('userId');
          const userName = currentUser?.name || localStorage.getItem('userName') || 'Unknown user';

          import('../services/employeeTrackingService').then(module => {
            const trackingService = module.default;
            trackingService.trackOrderShipped(
              order.orderId,
              order.items?.length || 0,
              {
                platform: 'shopify',
                method: 'home_delivery',
                trackingNumber: result.trackingNumber,
                carrier: 'Hsinchu Logistics',
                weight: result.weight,
                totalItems: order.items?.length || 0,
                user_id: userId,
                user_name: userName
              }
            );
          }).catch(err => {
            console.error('Error loading employee tracking service:', err);
          });
        } catch (trackingError) {
          console.error('Error tracking home delivery action:', trackingError);
        }
        // ✅ AUTOMATICALLY FULFILL THE ORDER
        try {
          enqueueSnackbar('正在標記訂單為已出貨...', { variant: 'info' });

          const fulfillResponse = await api.post(`/api/shopify/orders/${order.orderNumber}/fulfill`, {
            trackingNumber: result.trackingNumber,
            trackingCompany: 'Hsinchu Logistics'
          });

          if (fulfillResponse.data?.success) {
            // Update fulfillment status in UI
            const updateFulfillmentStatus = (prevOrders: any[]) => {
              return prevOrders.map(o => {
                if (o.orderId === order.orderId) {
                  return {
                    ...o,
                    fulfillment_status: 'fulfilled',
                    fulfillmentStatus: 'fulfilled',
                    fulfilled_at: new Date().toISOString(),
                    shopify_fulfillment_id: fulfillResponse.data.fulfillment?.id,
                    is_shipped: true
                  };
                }
                return o;
              });
            };

            //setParsedOrders(updateFulfillmentStatus);
            //setAllParsedOrders(updateFulfillmentStatus);

            enqueueSnackbar('訂單已自動標記為已出貨！', {
              variant: 'success',
              autoHideDuration: 4000
            });
          }
        } catch (fulfillError: any) {
          console.error('Auto-fulfill error:', fulfillError);
          enqueueSnackbar('宅配單已建立，但自動標記出貨失敗。請手動點擊「已出貨」按鈕。', {
            variant: 'warning',
            autoHideDuration: 6000
          });
        }

      } else {
        throw new Error(response.data.error || 'Failed to create shipping order');
      }

    } catch (error: any) {
      console.error('Error creating Hsinchu shipping order:', error);
      enqueueSnackbar('建立宅配出貨單失敗: ' + (error.response?.data?.error || error.message), {
        variant: 'error'
      });
    }
  }, [enqueueSnackbar, displayShippingLabel, setParsedOrders, setAllParsedOrders]);

  // Grouped products computation for product grouping view
  const groupedProducts = useMemo(() => {
    const groups: { [key: string]: { productName: string; sku: string; imageUrl: string; specs: { color: string; size: string; totalQty: number; orders: string[] }[]; totalQty: number; orderCount: number; key: string } } = {};

    const afterTimestamp = groupingAfterDate ? new Date(groupingAfterDate).getTime() / 1000 : 0;

    allParsedOrders.forEach(order => {
      if (afterTimestamp && order.createdTimestamp < afterTimestamp) return;
      order.items.forEach((item: OrderItem) => {
        if (item.received) return; // Only show unreceived items
        // Use SKU as primary key — SKU is unique per product, productName can collide across different products
        const key = item.sku || item.productName;
        if (!key) return;
        if (!groups[key]) {
          groups[key] = { productName: item.productName, sku: item.sku, imageUrl: item.imageUrl, specs: [], totalQty: 0, orderCount: 0, key };
        }
        // Parse itemSpec by "/" for Shopify (format: "Color / Size") and normalize
        const specParts = (item.itemSpec || '').split('/').map(s => s.trim());
        const color = specParts[0] || '-';
        const size = specParts[1] || '-';
        const specKey = `${color}|${size}`;
        let spec = groups[key].specs.find(s => `${s.color}|${s.size}` === specKey);
        if (!spec) {
          spec = { color, size, totalQty: 0, orders: [] };
          groups[key].specs.push(spec);
        }
        spec.totalQty += item.quantity;
        const orderNum = order.orderNumber;
        if (!spec.orders.includes(orderNum)) spec.orders.push(orderNum);
        groups[key].totalQty += item.quantity;
        if (!groups[key].imageUrl && item.imageUrl) groups[key].imageUrl = item.imageUrl;
      });
    });

    const result = Object.values(groups);
    // Size ordering map for standard clothing sizes
    const sizeOrder: { [key: string]: number } = { 'XS': 1, 'S': 2, 'M': 3, 'L': 4, 'XL': 5, '2XL': 6, '3XL': 7, '4XL': 8, '5XL': 9 };
    const getSizeRank = (size: string) => {
      // Extract letter size (e.g. "L (32)" -> "L", "XL (34)" -> "XL")
      const match = size.match(/^([\dX]*[SML]L?)/i);
      if (match && sizeOrder[match[1].toUpperCase()]) return sizeOrder[match[1].toUpperCase()];
      // Try numeric (e.g. "29", "30")
      const num = parseInt(size);
      if (!isNaN(num)) return 100 + num;
      return 500; // fallback
    };
    result.forEach(g => {
      const orderSet = new Set<string>();
      g.specs.forEach(s => s.orders.forEach(o => orderSet.add(o)));
      g.orderCount = orderSet.size;
      // Sort specs: by color first, then by size
      g.specs.sort((a, b) => {
        const colorCmp = a.color.localeCompare(b.color, 'zh-TW');
        if (colorCmp !== 0) return colorCmp;
        return getSizeRank(a.size) - getSizeRank(b.size);
      });
    });
    result.sort((a, b) => b.totalQty - a.totalQty);
    return result;
  }, [allParsedOrders, groupingAfterDate]);

  // Grouped product links for product grouping view
  const groupedProductLinks = useMemo(() => {
    const linkMap = new Map<string, ProductInfo>();
    const storedData = localStorage.getItem('productLinksData');
    if (!storedData) return linkMap;
    try {
      const products: ExcelProductData[] = JSON.parse(storedData);
      groupedProducts.forEach(group => {
        // Collect all unique SKUs for this product group
        const skus = new Set<string>();
        allParsedOrders.forEach(order => {
          order.items.forEach((item: OrderItem) => {
            if ((item.sku || item.productName) === group.key && item.sku) {
              skus.add(item.sku);
            }
          });
        });
        // Find the first matching product link from any SKU
        for (const sku of Array.from(skus)) {
          const match = products.find(p => p.id === sku);
          if (match) {
            linkMap.set(group.key, {
              link: decodeHtmlEntities(match.link || (match.allLinks && match.allLinks[0]) || ''),
              allLinks: match.allLinks?.map((l: string) => decodeHtmlEntities(l)),
            });
            break; // Use the first match found
          }
        }
      });
    } catch { }
    return linkMap;
  }, [groupedProducts, allParsedOrders, decodeHtmlEntities]);

  // Render product grouping view
  const renderProductGroupingView = () => {
    return (
      <Box sx={{ px: 3, pb: 3 }}>
        {/* Time Period Selector */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, p: 2, bgcolor: '#fafaf8', borderRadius: 2, border: '1px solid #e8e5e1' }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#5c5854', whiteSpace: 'nowrap' }}>篩選起始時間:</Typography>
          <TextField
            type="datetime-local"
            value={groupingAfterDate}
            onChange={(e) => setGroupingAfterDate(e.target.value)}
            size="small"
            sx={{ minWidth: 220 }}
            InputLabelProps={{ shrink: true }}
          />
          {groupingAfterDate && (
            <Button size="small" onClick={() => setGroupingAfterDate('')} variant="outlined" color="secondary">清除</Button>
          )}
          <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShoppingCartIcon sx={{ color: '#8b7355', fontSize: 20 }} />
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }}>
              {groupedProducts.length} 個產品，共 {groupedProducts.reduce((sum, g) => sum + g.totalQty, 0)} 件
            </Typography>
          </Box>
        </Box>

        {/* Open Links in Batches of 10 */}
        {groupedProducts.length > 0 && (() => {
          const allLinks = groupedProducts
            .map(g => groupedProductLinks.get(g.key))
            .map(info => info?.allLinks?.[0] || info?.link || '')
            .filter(Boolean);
          const remaining = allLinks.length - linkBatchIndex;
          const batchSize = Math.min(10, remaining);
          return (
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, position: 'sticky', top: 0, zIndex: 10, bgcolor: 'white', py: 1, borderBottom: '1px solid #e8e5e1' }}>
              <Button
                variant="outlined"
                startIcon={<OpenInNewIcon />}
                disabled={remaining <= 0}
                onClick={() => {
                  const batch = allLinks.slice(linkBatchIndex, linkBatchIndex + 10);
                  (async () => {
                    for (let i = 0; i < batch.length; i++) {
                      window.open(batch[i], '_blank', 'noopener,noreferrer');
                      if (i < batch.length - 1) await new Promise(r => setTimeout(r, 300));
                    }
                  })();
                  setLinkBatchIndex(prev => prev + batch.length);
                }}
                sx={{ borderColor: '#8b7355', color: '#8b7355', '&:hover': { bgcolor: '#f5f3f0', borderColor: '#6b5c42' }, textTransform: 'none' }}
              >
                開啟下{batchSize}個連結 ({linkBatchIndex}/{allLinks.length})
              </Button>
              {linkBatchIndex > 0 && (
                <Button
                  variant="text"
                  size="small"
                  onClick={() => setLinkBatchIndex(0)}
                  sx={{ textTransform: 'none', color: '#8c8580' }}
                >
                  重置
                </Button>
              )}
            </Box>
          );
        })()}

        {/* Product Cards */}
        {groupedProducts.map(group => {
          const linkInfo = groupedProductLinks.get(group.key);
          return (
            <React.Fragment key={group.key}>
              <Card
                elevation={0}
                sx={{ mb: 2, borderRadius: 3, border: '1px solid #e8e5e1', bgcolor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', overflow: 'hidden' }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start' }}>
                    {/* Product Image */}
                    {group.imageUrl && (
                      <Box sx={{ width: 80, height: 80, borderRadius: 2, overflow: 'hidden', flexShrink: 0, border: '1px solid #e8e5e1' }}>
                        <img src={group.imageUrl} alt={group.productName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </Box>
                    )}
                    {/* Product Info */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a', lineHeight: 1.3 }} noWrap>
                            {group.productName}
                          </Typography>
                          {group.sku && <Chip label={group.sku} size="small" sx={{ bgcolor: '#f0ede9', color: '#5c5854', fontWeight: 600 }} />}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip label={`共 ${group.totalQty} 件`} size="small" sx={{ bgcolor: '#dbeafe', color: '#1e40af', fontWeight: 700 }} />
                          <Chip label={`${group.orderCount} 筆訂單`} size="small" variant="outlined" sx={{ borderColor: '#d1cdc8', color: '#8c8580' }} />
                          {/* Link buttons */}
                          {linkInfo?.allLinks && linkInfo.allLinks.length > 0 ? (
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              {linkInfo.allLinks.map((link, idx) => (
                                <Tooltip key={idx} title={`開啟連結 ${idx + 1}`}>
                                  <IconButton
                                    onClick={() => window.open(link, '_blank', 'noopener,noreferrer')}
                                    sx={{ color: idx === 0 ? '#3b82f6' : '#8b7355', width: 36, height: 36, border: '1px solid', borderColor: idx === 0 ? '#bfdbfe' : '#d1cdc8', borderRadius: 1, '&:hover': { bgcolor: idx === 0 ? '#eff6ff' : '#f5f3f0' } }}
                                  >
                                    <OpenInNewIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </Tooltip>
                              ))}
                            </Box>
                          ) : linkInfo?.link ? (
                            <Tooltip title="開啟產品頁面">
                              <IconButton
                                onClick={() => window.open(linkInfo.link, '_blank', 'noopener,noreferrer')}
                                sx={{ color: '#3b82f6', width: 36, height: 36, border: '1px solid #bfdbfe', borderRadius: 1, '&:hover': { bgcolor: '#eff6ff' } }}
                              >
                                <OpenInNewIcon sx={{ fontSize: 18 }} />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                        </Box>
                      </Box>

                      {/* Variants Breakdown */}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1 }}>
                        {group.specs.map((spec, idx) => {
                          const specId = `${group.key}|${spec.color}|${spec.size}`;
                          const isExpanded = expandedGroupOrderIds.has(specId);
                          return (
                            <Chip
                              key={idx}
                              onClick={() => {
                                setExpandedGroupOrderIds(prev => {
                                  const next = new Set(prev);
                                  if (next.has(specId)) next.delete(specId); else next.add(specId);
                                  return next;
                                });
                              }}
                              label={
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <span style={{ fontWeight: 600 }}>{spec.color}{spec.size !== '-' ? ` ${spec.size}` : ''}</span>
                                  <span style={{ fontWeight: 700, color: '#3b82f6' }}>× {spec.totalQty}</span>
                                  <span style={{ fontSize: '0.7rem', color: '#8b7355', marginLeft: 2 }}>
                                    ({spec.orders.map(o => `#${o}`).join(', ')})
                                  </span>
                                  {isExpanded
                                    ? <ExpandLessIcon sx={{ fontSize: 14, color: '#8b7355', ml: 0.3 }} />
                                    : <ExpandMoreIcon sx={{ fontSize: 14, color: '#8c8580', ml: 0.3 }} />}
                                </Box>
                              }
                              variant="outlined"
                              size="small"
                              sx={{
                                height: 'auto', py: 0.5, borderRadius: '8px',
                                borderColor: isExpanded ? '#8b7355' : '#e8e5e1',
                                bgcolor: isExpanded ? '#f5f3f0' : 'transparent',
                                cursor: 'pointer',
                                '&:hover': { borderColor: '#8b7355', bgcolor: '#f5f3f0' },
                                '& .MuiChip-label': { whiteSpace: 'normal', px: 1 },
                              }}
                            />
                          );
                        })}
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              {/* Expanded Order Cards (inline) */}
              {(() => {
                // Collect orders from expanded specs only
                const expandedOrderNums = new Set<string>();
                group.specs.forEach(spec => {
                  const specId = `${group.key}|${spec.color}|${spec.size}`;
                  if (expandedGroupOrderIds.has(specId)) {
                    spec.orders.forEach(o => expandedOrderNums.add(o));
                  }
                });
                if (expandedOrderNums.size === 0) return null;
                return Array.from(expandedOrderNums).map((orderNum, idx) => {
                  const order = allParsedOrders.find(o => o.orderNumber === orderNum);
                  if (!order) return null;
                  const orderIndex = allParsedOrders.indexOf(order);
                  return (
                    <Box key={orderNum} sx={{ ml: 4, mb: 2, borderLeft: '3px solid #8b7355', borderRadius: '0 12px 12px 0' }}>
                      <OrderCard
                        order={order}
                        index={orderIndex >= 0 ? orderIndex : idx}
                        listRef={listRef}
                      />
                    </Box>
                  );
                });
              })()}
            </React.Fragment>
          );
        })}
      </Box>
    );
  };

  // Render orders list
  const renderOrdersList = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (parsedOrders.length === 0) {
      return (
        <Alert severity="info" sx={{ mt: 2 }}>
          No orders found. {!shopifyAuthenticated && 'Please connect your Shopify account.'}
        </Alert>
      );
    }

    const currentOrders = getCurrentPageOrders();

    return (
      <>
        <Box sx={{ flexShrink: 0, py: 1.5, px: { xs: 2, md: 3 } }}>
          <Stack spacing={2} direction="row" alignItems="center" justifyContent="space-between">
            <Typography sx={{ fontSize: '0.8rem', color: '#8c8580', fontWeight: 500 }}>
              <strong style={{ color: '#1a1a1a' }}>{(currentPage - 1) * ordersPerPage + 1}-{Math.min(currentPage * ordersPerPage, parsedOrders.length)}</strong> / {parsedOrders.length} 筆
            </Typography>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              showFirstButton
              showLastButton
              size="small"
              shape="rounded"
              sx={{
                '& .MuiPaginationItem-root': {
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  borderRadius: '8px',
                  minWidth: 32,
                  height: 32,
                  color: '#5c5854',
                  '&.Mui-selected': {
                    bgcolor: '#1a1a1a',
                    color: '#ffffff',
                    '&:hover': { bgcolor: '#333333' },
                  },
                },
              }}
            />
          </Stack>
        </Box>

        <Box sx={{
          width: '100%',
          height: { xs: 'calc(100vh - 300px)', md: '100vh' }, // Adjust for mobile
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          position: 'relative',
          '@media (max-width: 600px)': {
            height: 'calc(100vh - 250px)' // Account for filters on mobile
          }
        }}>
          <AutoSizer>
            {({ height, width }: { height: number; width: number }) => (
              <VariableSizeList
                itemKey={index => currentOrders[index].orderId}
                ref={listRef}
                height={height}
                width={width}
                itemCount={currentOrders.length}
                itemSize={index => getItemSize(index + (currentPage - 1) * ordersPerPage)}
                overscanCount={5}
              >
                {({ index, style }: { index: number; style: React.CSSProperties }) => {
                  const order = currentOrders[index];
                  const globalIndex = index + (currentPage - 1) * ordersPerPage;
                  return (
                    <div style={{
                      ...style,
                      paddingBottom: '20px', // Increased padding
                      paddingLeft: '8px',    // Add horizontal padding
                      paddingRight: '8px',   // Add horizontal padding
                      boxSizing: 'border-box', // Ensure padding doesn't affect width
                      minHeight: style.height // Ensure minimum height
                    }}>
                      <OrderCard
                        order={order}
                        index={globalIndex}
                        listRef={listRef}
                      />
                    </div>
                  );
                }}
              </VariableSizeList>
            )}
          </AutoSizer>
        </Box>




        {shippingDialogData && (
          <ShippingPreparationModal
            open={!!shippingDialogData}
            onClose={() => setShippingDialogData(null)}
            order={shippingDialogData || {}}
            enqueueSnackbar={enqueueSnackbar}
            onShippingPrepared={async (preparedData) => {
              console.log('Convenience store shipping prepared:', preparedData);

              // Close the dialog first
              setShippingDialogData(null);

              // Show loading message
              enqueueSnackbar('Updating tracking information...', { variant: 'info' });

              try {
                // ✅ Calculate display tracking number OUTSIDE the map function
                let displayTrackingNumber = '';
                if (preparedData.trackingNumber) {
                  displayTrackingNumber = preparedData.trackingNumber;
                } else if (preparedData.paymentNo) {
                  const logisticsType = preparedData.logisticsSubType || 'UNIMARTC2C';
                  if (logisticsType === 'FAMIC2C') {
                    displayTrackingNumber = preparedData.paymentNo;
                  } else {
                    displayTrackingNumber = preparedData.validationNo
                      ? `${preparedData.paymentNo}${preparedData.validationNo}`
                      : preparedData.paymentNo;
                  }
                }

                // ✅ IMMEDIATELY update with the prepared data we just received
                const updateOrdersWithTracking = (prevOrders: any[]) => {
                  return prevOrders.map(order => {
                    if (order.orderId === shippingDialogData.orderId) {
                      // Update all items with the new tracking info
                      const updatedItems = order.items.map((item: OrderItem) => ({
                        ...item,
                        trackingNumber: preparedData.trackingNumber || preparedData.logisticsId,
                        paymentNo: preparedData.paymentNo,
                        cvsValidationNo: preparedData.validationNo,
                        ecpayLogisticsId: preparedData.logisticsId,
                        logistics_sub_type: preparedData.logisticsSubType || order.logistics_sub_type,
                        store_id: preparedData.storeId || order.store_id,
                        store_name: preparedData.storeName || order.store_name
                      }));

                      return {
                        ...order,
                        items: updatedItems,
                        hasTrackingInfo: true,
                        tracking_number: displayTrackingNumber, // ✅ Now this variable is accessible
                        payment_no: preparedData.paymentNo,
                        cvs_validation_no: preparedData.validationNo,
                        ecpay_logistics_id: preparedData.logisticsId,
                        logistics_sub_type: preparedData.logisticsSubType || order.logistics_sub_type,
                        store_id: preparedData.storeId || order.store_id,
                        store_name: preparedData.storeName || order.store_name
                      };
                    }
                    return order;
                  });
                };

                // Update both state arrays immediately
                setParsedOrders(updateOrdersWithTracking);
                setAllParsedOrders(updateOrdersWithTracking);

                enqueueSnackbar(`超商出貨成功！追蹤號碼: ${displayTrackingNumber || preparedData.paymentNo}`, {
                  variant: 'success',
                  autoHideDuration: 6000
                });


                try {
                  const userId = currentUser?.id?.toString() || localStorage.getItem('userId');
                  const userName = currentUser?.name || localStorage.getItem('userName') || 'Unknown user';

                  import('../services/employeeTrackingService').then(module => {
                    const trackingService = module.default;
                    trackingService.trackOrderShipped(
                      shippingDialogData.orderId,
                      shippingDialogData.items?.length || 0,
                      {
                        platform: 'shopify',
                        method: 'convenience_store',
                        trackingNumber: displayTrackingNumber || preparedData.paymentNo,
                        logisticsType: preparedData.logisticsSubType,
                        logisticsId: preparedData.logisticsId,
                        storeId: preparedData.storeId,
                        storeName: preparedData.storeName,
                        totalItems: shippingDialogData.items?.length || 0,
                        user_id: userId,
                        user_name: userName
                      }
                    );
                  }).catch(err => {
                    console.error('Error loading employee tracking service:', err);
                  });
                } catch (trackingError) {
                  console.error('Error tracking convenience store shipping:', trackingError);
                }

              } catch (error) {
                console.error('Error updating tracking information:', error);
                enqueueSnackbar('Failed to update tracking info. Please refresh to see changes.', { variant: 'warning' });

                // Fallback: trigger a full refresh
                await parseShopifyOrders();
              }
            }}
          />
        )}

        {/* Home Delivery Modal */}
        {homeDeliveryDialogData && (
          <HomeDeliveryModal
            open={!!homeDeliveryDialogData}
            onClose={() => setHomeDeliveryDialogData(null)}
            order={homeDeliveryDialogData || {}}
            onDeliveryPrepared={async (deliveryData) => {
              console.log('Home delivery prepared:', deliveryData);

              // Close the dialog first
              setHomeDeliveryDialogData(null);

              // Show loading message
              enqueueSnackbar('Updating delivery information...', { variant: 'info' });

              try {
                // Update the orders with new delivery data
                const updateOrdersWithDelivery = (prevOrders: any[]) => {
                  return prevOrders.map(order => {
                    if (order.orderId === homeDeliveryDialogData.orderId) {
                      // Update all items with tracking info
                      const updatedItems = order.items.map((item: OrderItem) => ({
                        ...item,
                        trackingNumber: deliveryData.trackingNumber,
                        deliveryMethod: 'home_delivery',
                        carrier: deliveryData.carrier
                      }));

                      return {
                        ...order,
                        items: updatedItems,
                        hasTrackingInfo: true,
                        tracking_number: deliveryData.trackingNumber,
                        delivery_method: 'home_delivery',
                        carrier: deliveryData.carrier,
                        estimatedDeliveryDate: deliveryData.estimatedDeliveryDate,
                        deliveryNotes: deliveryData.deliveryNotes
                      };
                    }
                    return order;
                  });
                };

                // Update both state arrays
                setParsedOrders(updateOrdersWithDelivery);
                setAllParsedOrders(updateOrdersWithDelivery);

                enqueueSnackbar('宅配出貨資訊更新成功！', { variant: 'success' });

              } catch (error) {
                console.error('Error updating delivery information:', error);
                enqueueSnackbar('Failed to update delivery info. Please refresh to see changes.', { variant: 'warning' });
              }
            }}
          />
        )}
      </>
    );
  };

  return (
    <Box sx={S.page}>
      <Box sx={S.header}>
        <Typography component="h1" sx={S.headerTitle}>
          Shopify 訂單管理
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading || fetchingRef.current}
            sx={S.headerBtn}
            size="small"
          >
            {loading ? <CircularProgress size={18} color="inherit" /> : '重新整理'}
          </Button>

          <Box sx={S.tabContainer}>
            <Button
              size="small"
              startIcon={<ViewListIcon />}
              onClick={() => setViewMode('orders')}
              sx={S.tabButton(viewMode === 'orders')}
            >
              訂單
            </Button>
            <Button
              size="small"
              startIcon={<CategoryIcon />}
              onClick={() => setViewMode('products')}
              sx={S.tabButton(viewMode === 'products')}
            >
              叫貨
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Filter Controls */}
      <Box sx={S.filterPanel}>
        <Box sx={{ mb: 2 }}>
          <TextField
            value={searchInput}
            label="搜尋訂單"
            placeholder="標籤編號、商品名稱、訂單號（#1369）、規格或客戶名稱"
            variant="outlined"
            size="medium"
            fullWidth
            onChange={handleSearchChange}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                triggerSearch();
              }
            }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: '#8c8580' }} />,
              endAdornment: (
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {isSearchPending && (
                    <Tooltip title="搜尋中...">
                      <CircularProgress size={16} sx={{ mr: 1 }} />
                    </Tooltip>
                  )}
                  {searchInput && (
                    <IconButton size="small" onClick={clearSearch}>
                      <CloseIcon />
                    </IconButton>
                  )}
                </Box>
              )
            }}
            sx={S.searchField}
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl sx={{ minWidth: 140, ...S.filterSelect }}>
            <InputLabel id="status-filter-label">付款狀態</InputLabel>
            <Select
              labelId="status-filter-label"
              value={statusFilter}
              label="付款狀態"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="All">全部</MenuItem>
              <MenuItem value="pending">待付款</MenuItem>
              <MenuItem value="paid">已付款</MenuItem>
              <MenuItem value="partially_paid">部分付款</MenuItem>
              <MenuItem value="refunded">已退款</MenuItem>
              <MenuItem value="voided">已作廢</MenuItem>
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Checkbox
                checked={showHiddenOrders}
                onChange={(e) => setShowHiddenOrders(e.target.checked)}
                size="small"
              />
            }
            label={<Typography sx={{ fontSize: '0.85rem', color: '#5c5854' }}>隱藏訂單</Typography>}
          />

          <FormControl sx={{ minWidth: 140, ...S.filterSelect }}>
            <InputLabel id="fulfillment-filter-label">出貨狀態</InputLabel>
            <Select
              labelId="fulfillment-filter-label"
              value={fulfillmentFilter}
              label="出貨狀態"
              onChange={(e) => setFulfillmentFilter(e.target.value)}
            >
              <MenuItem value="All">全部訂單</MenuItem>
              <MenuItem value="unfulfilled">未出貨</MenuItem>
              <MenuItem value="fulfilled">已出貨</MenuItem>
              <MenuItem value="partially_fulfilled">部分出貨</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 120, ...S.filterSelect }}>
            <InputLabel id="color-filter-label">顏色</InputLabel>
            <Select
              labelId="color-filter-label"
              value={colorFilter}
              label="顏色"
              onChange={(e) => setColorFilter(e.target.value)}
            >
              <MenuItem value="All">全部</MenuItem>
              {getUniqueColorsAndSizes.colors.map(color => (
                <MenuItem key={color} value={color}>{color}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 110, ...S.filterSelect }}>
            <InputLabel id="size-filter-label">尺寸</InputLabel>
            <Select
              labelId="size-filter-label"
              value={sizeFilter}
              label="尺寸"
              onChange={(e) => setSizeFilter(e.target.value)}
            >
              <MenuItem value="All">全部</MenuItem>
              {getUniqueColorsAndSizes.sizes.map(size => (
                <MenuItem key={size} value={size}>{size}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 120, ...S.filterSelect }}>
            <InputLabel id="tag-filter-label">標籤</InputLabel>
            <Select
              labelId="tag-filter-label"
              value={tagFilter}
              label="標籤"
              onChange={(e) => setTagFilter(e.target.value)}
            >
              <MenuItem value="All">所有</MenuItem>
              <MenuItem value="NumbersOnly">僅數字</MenuItem>
              <MenuItem value="NoNumbers">非數字</MenuItem>
              <MenuItem value="NoTag">無標籤</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 120, ...S.filterSelect }}>
            <InputLabel id="sort-order-label">排序</InputLabel>
            <Select
              labelId="sort-order-label"
              value={sortOrder}
              label="排序"
              onChange={(e) => setSortOrder(e.target.value)}
              startAdornment={<SortIcon sx={{ mr: 1, color: '#8c8580' }} />}
            >
              <MenuItem value="newest">最新優先</MenuItem>
              <MenuItem value="oldest">最舊優先</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 130, ...S.filterSelect }}>
            <InputLabel id="received-status-filter-label">收貨狀態</InputLabel>
            <Select
              labelId="received-status-filter-label"
              value={receivedStatusFilter}
              label="收貨狀態"
              onChange={(e) => { setReceivedStatusFilter(e.target.value); setJustCompletedOrders({}); }}
            >
              <MenuItem value="All">全部</MenuItem>
              <MenuItem value="ready-to-ship">可出貨</MenuItem>
              <MenuItem value="complete">已收齊</MenuItem>
              <MenuItem value="not-received">未收到</MenuItem>
              <MenuItem value="overdue">超過10天未出貨</MenuItem>
            </Select>
          </FormControl>

          {(searchFilter || colorFilter !== 'All' || sizeFilter !== 'All' || tagFilter !== 'All' || statusFilter !== 'All' || receivedStatusFilter !== 'All') && (
            <Button
              variant="outlined"
              size="small"
              onClick={clearAllFilters}
              startIcon={<CloseIcon />}
              sx={{ ...S.headerBtn, height: 36 }}
            >
              清除
            </Button>
          )}

          <Box sx={{ ml: 'auto', textAlign: 'right' }}>
            {(searchFilter || colorFilter !== 'All' || sizeFilter !== 'All' || tagFilter !== 'All') && (
              <Box sx={{ display: 'flex', gap: 0.75, mb: 0.5, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                {searchFilter && (
                  <Chip label={`搜尋: ${searchFilter}`} color="primary" size="small" onDelete={() => setSearchFilter('')} sx={S.filterChip} />
                )}
                {colorFilter !== 'All' && (
                  <Chip label={`顏色: ${colorFilter}`} color="secondary" size="small" onDelete={() => setColorFilter('All')} sx={S.filterChip} />
                )}
                {sizeFilter !== 'All' && (
                  <Chip label={`尺寸: ${sizeFilter}`} color="info" size="small" onDelete={() => setSizeFilter('All')} sx={S.filterChip} />
                )}
                {tagFilter !== 'All' && (
                  <Chip
                    label={tagFilter === 'NumbersOnly' ? '僅數字標籤' : tagFilter === 'NoNumbers' ? '非數字標籤' : tagFilter === 'NoTag' ? '無標籤' : `標籤: ${tagFilter}`}
                    color="warning" size="small" onDelete={() => setTagFilter('All')} sx={S.filterChip}
                  />
                )}
              </Box>
            )}
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: '#5c5854' }}>
              共 {parsedOrders.length} 筆訂單
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Dashboard Summary Stats */}
      {(() => {
        const visibleOrders = allParsedOrders.filter(o => !o.is_hidden && o.status !== 'refunded');
        const totalCount = visibleOrders.length;
        const pendingCount = visibleOrders.filter(o => o.receivedStatus === 'pending' && !o.allItemsReceived).length;
        const partialCount = visibleOrders.filter(o => o.receivedStatus === 'partial').length;
        const readyCount = visibleOrders.filter(o => o.items?.every((item: { received: any }) => item.received)).length;
        const shippedCount = visibleOrders.filter(o => o.hasTrackingInfo).length;
        const statItems = [
          { label: '待處理', count: pendingCount, color: '#ef4444', bg: '#fef2f2', border: '#fecaca' },
          { label: '部分收到', count: partialCount, color: '#d97706', bg: '#fffbeb', border: '#fde68a' },
          { label: '可出貨', count: readyCount, color: '#16a34a', bg: '#f0fdf4', border: '#bbf7d0' },
          { label: '已建物流單', count: shippedCount, color: '#2563eb', bg: '#eff6ff', border: '#bfdbfe' },
        ];
        return (
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
            gap: 1.5, mb: 2, mx: { xs: 2, md: 3 },
          }}>
            {statItems.map(s => (
              <Box key={s.label} sx={{
                px: 2, py: 1.5,
                bgcolor: s.bg,
                borderRadius: '12px',
                border: `1px solid ${s.border}`,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                transition: 'transform 0.15s ease',
                '&:hover': { transform: 'translateY(-1px)' },
              }}>
                <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: s.color, lineHeight: 1, fontFamily: '"DM Sans", sans-serif' }}>
                  {s.count}
                </Typography>
                <Box>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: s.color, lineHeight: 1.2, letterSpacing: '0.02em' }}>
                    {s.label}
                  </Typography>
                  <Typography sx={{ fontSize: '0.65rem', color: s.color, opacity: 0.6, lineHeight: 1.2, mt: 0.25 }}>
                    {totalCount > 0 ? `${Math.round((s.count / totalCount) * 100)}%` : '0%'}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        );
      })()}

      {/* Batch Actions Toolbar */}
      <BatchActionsToolbar />

      {viewMode === 'products' ? renderProductGroupingView() : renderOrdersList()}

      {/* Batch Print Dialog */}
      <BatchPrintDialog />
    </Box>
  );
};

export default ShopifyOrdersPage;